import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../components/auth/AuthContext";
import { HeaderBar } from "../components/layout/TopBar";
import NavBar from "../components/layout/NavBar";
import {
  getWorkerStatus,
  getWorkerMetrics,
  getBatchProcessingStats,
  createWorker,
  deployWorker,
  startWorker,
  stopWorker,
  pauseWorker,
  makeWorkerOffline,
  deleteWorker,
  checkWorkerService,
  uninstallWorkerService,
  getWorkerLogs,
  getDeploymentConfiguration,
  updateDeploymentConfiguration,
  workerLiveLogUrl,
  getWorkerAppSettings,
  updateWorkerAppSettings,
  getGathererStatus,
  getGathererDeploymentConfiguration,
  updateGathererDeploymentConfiguration,
  createGatherer,
  startGatherer,
  stopGatherer,
  deleteGatherer,
  checkGathererService,
  uninstallGathererService,
  getGathererAppSettings,
  updateGathererAppSettings,
  gathererLiveLogUrl,
} from "../api/workerApi";
import { getHosts } from "../api/hostApi";
import { friendlyMessageForApiError } from "../api/errorUtils";
import { Hero, ToggleSwitch } from "../components/adminSurface/AdminSurface";
import { PulseArt } from "../components/adminSurface/heroArt";

export default function WorkerMonitoring() {
  const { t } = useTranslation();
  const { token } = useAuth();

  const [workerStatus, setWorkerStatus] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [batchStats, setBatchStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [timeRange, setTimeRange] = useState(60); // minutes
  const [showCreateWorker, setShowCreateWorker] = useState(false);
  const [newWorkerName, setNewWorkerName] = useState("");
  const [actionInProgress, setActionInProgress] = useState(false);
  const [selectedWorkerLogs, setSelectedWorkerLogs] = useState(null);
  const [workerLogs, setWorkerLogs] = useState(null);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [logsFilter, setLogsFilter] = useState("All");
  const [logsLimit, setLogsLimit] = useState(100);
  const [hosts, setHosts] = useState([]);
  const [selectedHostId, setSelectedHostId] = useState(null);
  const [showHostSelector, setShowHostSelector] = useState(false);
  const [deployingWorkerId, setDeployingWorkerId] = useState(null);
  const [showGathererHostSelector, setShowGathererHostSelector] = useState(false);
  const [selectedGathererHostId, setSelectedGathererHostId] = useState(null);
  const [deployingGathererId, setDeployingGathererId] = useState(null);
  const [deploymentConfig, setDeploymentConfig] = useState({
    mode: "LocalProcess",
    enabled: false,
    localWorkerPath: "",
  });
  const [showDeploymentSettings, setShowDeploymentSettings] = useState(false);
  const [savingDeploymentConfig, setSavingDeploymentConfig] = useState(false);
  const [deploymentConfigSaved, setDeploymentConfigSaved] = useState(false);

  // Worker app settings modal state
  const [workerSettingsModal, setWorkerSettingsModal] = useState(null); // { id, name }
  const [workerSettings, setWorkerSettings] = useState(null);
  const [loadingWorkerSettings, setLoadingWorkerSettings] = useState(false);
  const [savingWorkerSettings, setSavingWorkerSettings] = useState(false);
  const [workerSettingsSaved, setWorkerSettingsSaved] = useState(false);
  const [workerSettingsError, setWorkerSettingsError] = useState("");
  const [showWorkerSecrets, setShowWorkerSecrets] = useState(false);

  // Confirm modal state
  const [confirmModal, setConfirmModal] = useState(null); // { message, workerName, icon, onConfirm }
  const [serviceCheck, setServiceCheck] = useState(null); // { workerName, message, exists, running, ... }

  // Gatherer state
  const [gathererStatus, setGathererStatus] = useState(null);
  const [gathererDeploymentConfig, setGathererDeploymentConfig] = useState({
    mode: "LocalProcess",
    enabled: false,
    localGathererPath: "",
  });
  const [showGathererDeploymentSettings, setShowGathererDeploymentSettings] = useState(false);
  const [savingGathererConfig, setSavingGathererConfig] = useState(false);
  const [gathererConfigSaved, setGathererConfigSaved] = useState(false);
  const [showCreateGatherer, setShowCreateGatherer] = useState(false);
  const [newGathererName, setNewGathererName] = useState("");
  const [gathererActionInProgress, setGathererActionInProgress] = useState(false);
  const [gathererError, setGathererError] = useState("");
  const [gathererSettingsModal, setGathererSettingsModal] = useState(null); // { id, name }
  const [gathererSettings, setGathererSettings] = useState(null);
  const [loadingGathererSettings, setLoadingGathererSettings] = useState(false);
  const [savingGathererSettings, setSavingGathererSettings] = useState(false);
  const [gathererSettingsSaved, setGathererSettingsSaved] = useState(false);
  const [gathererSettingsError, setGathererSettingsError] = useState("");

  // Worker live log streaming state
  const [liveLogsWorker, setLiveLogsWorker] = useState(null); // { id, workerGuid, name }
  const [liveLines, setLiveLines] = useState([]);
  const liveLogEndRef = useRef(null);
  const liveStreamAbortRef = useRef(null);

  // Gatherer live log streaming state
  const [liveLogsGatherer, setLiveLogsGatherer] = useState(null); // { id, gathererGuid, name }
  const [gathererLiveLines, setGathererLiveLines] = useState([]);
  const gathererLiveLogEndRef = useRef(null);
  const gathererLiveStreamAbortRef = useRef(null);

  const fetchData = async () => {
    if (!token) return;
    setLoading(true);
    setError("");
    try {
      const [status, metricsData, stats, hostsData, deployConfig, gathererStatusData, gathererDeployConfig] = await Promise.all([
        getWorkerStatus(token),
        getWorkerMetrics(token, timeRange),
        getBatchProcessingStats(token),
        getHosts(token),
        getDeploymentConfiguration(token),
        getGathererStatus(token),
        getGathererDeploymentConfiguration(token),
      ]);
      setWorkerStatus(status);
      setMetrics(metricsData);
      setBatchStats(stats);
      setHosts(hostsData);
      if (deployConfig) setDeploymentConfig(deployConfig);
      setGathererStatus(gathererStatusData);
      if (gathererDeployConfig) setGathererDeploymentConfig(gathererDeployConfig);
    } catch (err) {
      setError(friendlyMessageForApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [token, timeRange]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(fetchData, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, [autoRefresh, token, timeRange]);

  const getStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case "running": case "active": return "running";
      case "idle":                   return "idle";
      case "stopped": case "error":  return "stopped";
      default:                       return "default";
    }
  };

  const handleCreateWorker = async () => {
    if (!newWorkerName.trim()) {
      setError(
        t("worker.nameRequired", { defaultValue: "Worker name is required" })
      );
      return;
    }

    setActionInProgress(true);
    setError("");
    try {
      await createWorker(token, { workerName: newWorkerName });
      setNewWorkerName("");
      setShowCreateWorker(false);
      await fetchData();
    } catch (err) {
      setError(friendlyMessageForApiError(err));
    } finally {
      setActionInProgress(false);
    }
  };

  const handleSaveDeploymentConfig = async () => {
    setSavingDeploymentConfig(true);
    setError("");
    try {
      await updateDeploymentConfiguration(token, deploymentConfig);
      setDeploymentConfigSaved(true);
      setTimeout(() => setDeploymentConfigSaved(false), 3000);
    } catch (err) {
      setError(friendlyMessageForApiError(err));
    } finally {
      setSavingDeploymentConfig(false);
    }
  };

  const deploymentMode = (deploymentConfig?.mode || "LocalProcess").toLowerCase();
  const isLocalProcess = deploymentMode === "localprocess";
  const isServiceMode = deploymentMode === "service";
  // Docker and Service both run somewhere other than "wherever the API happens to be", so both ask which
  // host. Local process is the only mode with nowhere else to go.
  const modeUsesHosts = !isLocalProcess;

  const handleStartWorker = async (
    workerId,
    workerName,
    targetHostId = null
  ) => {
    if (!deploymentConfig.enabled) {
      setError("Worker deployment is disabled. Enable it in Deployment Settings below.");
      return;
    }

    // Docker and Service modes: show host selector when hosts are configured
    if (modeUsesHosts && hosts.length > 0 && targetHostId === null && !showHostSelector) {
      setDeployingWorkerId(workerId);
      setShowHostSelector(true);
      return;
    }

    const targetHostName = targetHostId
      ? hosts.find((h) => h.id === targetHostId)?.hostName || "selected host"
      : "default host";

    const confirmMsg = isLocalProcess
      ? `Start worker '${workerName}' as a local .NET process on this machine (${window.location.hostname}).`
      : isServiceMode
      ? `Register and start worker '${workerName}' as a native service on ${targetHostName}. ` +
        `The Depository installer must already have been run there.`
      : `Deploy and start worker '${workerName}' as a Docker container on ${targetHostName}.`;

    setConfirmModal({
      message: confirmMsg,
      workerName,
      icon: "▶",
      onConfirm: async () => {
        setConfirmModal(null);
        setActionInProgress(true);
        setError("");
        try {
          await startWorker(token, workerId, targetHostId);
          setShowHostSelector(false);
          setDeployingWorkerId(null);
          setSelectedHostId(null);
          await fetchData();
        } catch (err) {
          setError(friendlyMessageForApiError(err));
        } finally {
          setActionInProgress(false);
        }
      },
    });
  };

  const handleStopWorker = async (workerId, workerName) => {
    setActionInProgress(true);
    setError("");
    try {
      await stopWorker(token, workerId);
      await fetchData();
    } catch (err) {
      setError(friendlyMessageForApiError(err));
    } finally {
      setActionInProgress(false);
    }
  };

  const handlePauseWorker = async (workerId, workerName) => {
    setActionInProgress(true);
    setError("");
    try {
      await pauseWorker(token, workerId);
      await fetchData();
    } catch (err) {
      setError(friendlyMessageForApiError(err));
    } finally {
      setActionInProgress(false);
    }
  };

  const handleMakeOffline = (workerId, workerName) => {
    setConfirmModal({
      message: t("worker.confirmOffline", {
        workerName,
        defaultValue: `Make worker '${workerName}' offline?`,
      }),
      workerName,
      icon: "⊘",
      onConfirm: async () => {
        setConfirmModal(null);
        setActionInProgress(true);
        setError("");
        try {
          await makeWorkerOffline(token, workerId);
          await fetchData();
        } catch (err) {
          setError(friendlyMessageForApiError(err));
        } finally {
          setActionInProgress(false);
        }
      },
    });
  };

  const isServiceWorker = (worker) =>
    ["windowsservice", "systemdservice"].includes(
      (worker?.deploymentType || "").toLowerCase()
    );

  const handleDeleteWorker = (workerId, workerName, worker = null) => {
    // Deleting the row and unregistering the service on the host are separate decisions: the second
    // reaches across the network and removes installed software, so it is asked for, never assumed.
    const serviceBacked = isServiceWorker(worker);
    setConfirmModal({
      message: serviceBacked
        ? t("worker.confirmDeleteWithService", {
            workerName,
            hostName: worker?.hostName || "",
            defaultValue: `Delete worker '${workerName}'? Its service on ${worker?.hostName || "the host"} will also be unregistered. This cannot be undone.`,
          })
        : t("worker.confirmDelete", {
            workerName,
            defaultValue: `Delete worker '${workerName}'? This cannot be undone.`,
          }),
      workerName,
      icon: "×",
      onConfirm: async () => {
        setConfirmModal(null);
        setActionInProgress(true);
        setError("");
        try {
          await deleteWorker(token, workerId, serviceBacked);
          await fetchData();
        } catch (err) {
          setError(friendlyMessageForApiError(err));
        } finally {
          setActionInProgress(false);
        }
      },
    });
  };

  // What the host's own service manager says, as opposed to what the heartbeat says. Answers the case
  // the worker list cannot: a unit registered and "active" with the worker inside it wedged, or one
  // removed on the host by hand.
  const handleCheckService = async (worker) => {
    setActionInProgress(true);
    setError("");
    try {
      const result = await checkWorkerService(token, worker.id);
      setServiceCheck({ workerName: worker.workerName, ...result });
    } catch (err) {
      setError(friendlyMessageForApiError(err));
    } finally {
      setActionInProgress(false);
    }
  };

  const handleUninstallService = (worker) => {
    setConfirmModal({
      message: t("worker.confirmUninstallService", {
        serviceName: worker.serviceName || "",
        hostName: worker.hostName || "",
        defaultValue: `Unregister service '${worker.serviceName}' on ${worker.hostName}? The worker stays in this list and can be redeployed.`,
      }),
      workerName: worker.workerName,
      icon: "×",
      onConfirm: async () => {
        setConfirmModal(null);
        setActionInProgress(true);
        setError("");
        try {
          await uninstallWorkerService(token, worker.id);
          await fetchData();
        } catch (err) {
          setError(friendlyMessageForApiError(err));
        } finally {
          setActionInProgress(false);
        }
      },
    });
  };

  const handleViewLogs = async (workerId, workerName) => {
    setSelectedWorkerLogs({ id: workerId, name: workerName });
    setLoadingLogs(true);
    setWorkerLogs(null);
    setLogsFilter("All");
    setLogsLimit(100);
    try {
      const logs = await getWorkerLogs(token, workerId, 100);
      setWorkerLogs(logs);
    } catch (err) {
      setError(friendlyMessageForApiError(err));
    } finally {
      setLoadingLogs(false);
    }
  };

  const handleLoadMoreLogs = async () => {
    const newLimit = logsLimit + 100;
    setLogsLimit(newLimit);
    setLoadingLogs(true);
    try {
      const logs = await getWorkerLogs(token, selectedWorkerLogs.id, newLimit);
      setWorkerLogs(logs);
    } catch (err) {
      setError(friendlyMessageForApiError(err));
    } finally {
      setLoadingLogs(false);
    }
  };

  const handleCloseLogs = () => {
    setSelectedWorkerLogs(null);
    setWorkerLogs(null);
    setLogsFilter("All");
    setLogsLimit(100);
  };

  const handleOpenLiveLogs = (worker) => {
    // Abort any previous stream
    if (liveStreamAbortRef.current) liveStreamAbortRef.current.abort();
    setLiveLines([]);
    setLiveLogsWorker({ id: worker.id, workerGuid: worker.workerId, name: worker.workerName });
  };

  const handleCloseLiveLogs = () => {
    if (liveStreamAbortRef.current) liveStreamAbortRef.current.abort();
    setLiveLogsWorker(null);
    setLiveLines([]);
  };

  const handleOpenWorkerSettings = async (worker) => {
    setWorkerSettingsModal({ id: worker.id, name: worker.workerName });
    setWorkerSettings(null);
    setWorkerSettingsError("");
    setWorkerSettingsSaved(false);
    setShowWorkerSecrets(false);
    setLoadingWorkerSettings(true);
    try {
      const settings = await getWorkerAppSettings(token, worker.id);
      setWorkerSettings(settings);
    } catch (err) {
      setWorkerSettingsError(friendlyMessageForApiError(err));
    } finally {
      setLoadingWorkerSettings(false);
    }
  };

  const handleCloseWorkerSettings = () => {
    setWorkerSettingsModal(null);
    setWorkerSettings(null);
    setWorkerSettingsError("");
    setWorkerSettingsSaved(false);
    setShowWorkerSecrets(false);
  };

  const handleSaveWorkerSettings = async () => {
    if (!workerSettingsModal || !workerSettings) return;
    setSavingWorkerSettings(true);
    setWorkerSettingsError("");
    try {
      await updateWorkerAppSettings(token, workerSettingsModal.id, workerSettings);
      setWorkerSettingsSaved(true);
      setTimeout(() => setWorkerSettingsSaved(false), 3000);
    } catch (err) {
      setWorkerSettingsError(friendlyMessageForApiError(err));
    } finally {
      setSavingWorkerSettings(false);
    }
  };

  // ── Gatherer handlers ────────────────────────────────────────────────────

  const gathererMode = (gathererDeploymentConfig?.mode || "LocalProcess").toLowerCase();
  const isGathererLocalProcess = gathererMode === "localprocess";
  const isGathererServiceMode = gathererMode === "service";
  const isGathererService = (g) =>
    ["windowsservice", "systemdservice"].includes((g?.deploymentType || "").toLowerCase());

  // One host-picker serves both workers and gatherers. Two near-identical modals would be two places to
  // fix the next time the host card needs to say something new.
  const hostSelectorOpen = showHostSelector || showGathererHostSelector;
  const hostSelectorIsGatherer = showGathererHostSelector;
  const hostSelectorServiceMode = hostSelectorIsGatherer ? isGathererServiceMode : isServiceMode;
  const hostSelectorValue = hostSelectorIsGatherer ? selectedGathererHostId : selectedHostId;

  const setHostSelectorValue = (id) =>
    hostSelectorIsGatherer ? setSelectedGathererHostId(id) : setSelectedHostId(id);

  const closeHostSelector = () => {
    setShowHostSelector(false);
    setShowGathererHostSelector(false);
    setDeployingWorkerId(null);
    setDeployingGathererId(null);
    setSelectedHostId(null);
    setSelectedGathererHostId(null);
  };

  const confirmHostSelector = () => {
    if (hostSelectorIsGatherer) {
      const gatherer = gathererStatus?.gathererInstances?.find((g) => g.id === deployingGathererId);
      if (gatherer) {
        handleStartGatherer(gatherer.id, gatherer.gathererName, selectedGathererHostId);
      }
      return;
    }
    const worker = workerStatus?.workerInstances?.find((w) => w.id === deployingWorkerId);
    if (worker) {
      handleStartWorker(worker.id, worker.workerName, selectedHostId);
    }
  };

  const handleSaveGathererConfig = async () => {
    setSavingGathererConfig(true);
    setGathererError("");
    try {
      await updateGathererDeploymentConfiguration(token, gathererDeploymentConfig);
      setGathererConfigSaved(true);
      setTimeout(() => setGathererConfigSaved(false), 3000);
    } catch (err) {
      setGathererError(friendlyMessageForApiError(err));
    } finally {
      setSavingGathererConfig(false);
    }
  };

  const handleCreateGatherer = async () => {
    if (!newGathererName.trim()) {
      setGathererError("Gatherer name is required");
      return;
    }
    setGathererActionInProgress(true);
    setGathererError("");
    try {
      await createGatherer(token, { gathererName: newGathererName });
      setNewGathererName("");
      setShowCreateGatherer(false);
      await fetchData();
    } catch (err) {
      setGathererError(friendlyMessageForApiError(err));
    } finally {
      setGathererActionInProgress(false);
    }
  };

  const handleStartGatherer = async (gathererId, gathererName, targetHostId = null) => {
    setGathererError("");

    // Native-service mode is the only gatherer mode that can target another machine: the Docker path
    // runs docker on the API's own host regardless of what is selected.
    if (
      isGathererServiceMode &&
      gathererDeploymentConfig.enabled &&
      hosts.length > 0 &&
      targetHostId === null &&
      !showGathererHostSelector
    ) {
      setDeployingGathererId(gathererId);
      setShowGathererHostSelector(true);
      return;
    }

    const gathererHostName = targetHostId
      ? hosts.find((h) => h.id === targetHostId)?.hostName || "selected host"
      : "this machine";

    setConfirmModal({
      message: gathererDeploymentConfig.enabled
        ? (isGathererLocalProcess
            ? `Start gatherer '${gathererName}' as a local process on this machine.`
            : isGathererServiceMode
            ? `Register and start gatherer '${gathererName}' as a native service on ${gathererHostName}. ` +
              `The Depository installer must already have been run there.`
            : `Deploy gatherer '${gathererName}' as a Docker container.`)
        : `Gatherer deployment is disabled. Enable it in Gatherer Deployment Settings, then start '${gathererName}'.`,
      workerName: gathererName,
      icon: "▶",
      onConfirm: async () => {
        if (!gathererDeploymentConfig.enabled) {
          setConfirmModal(null);
          setGathererError("Gatherer deployment is disabled. Open Gatherer Deployment Settings above and enable it first.");
          return;
        }
        setConfirmModal(null);
        setGathererActionInProgress(true);
        setGathererError("");
        try {
          await startGatherer(token, gathererId, targetHostId);
          setShowGathererHostSelector(false);
          setDeployingGathererId(null);
          setSelectedGathererHostId(null);
          await fetchData();
        } catch (err) {
          setGathererError(friendlyMessageForApiError(err));
        } finally {
          setGathererActionInProgress(false);
        }
      },
    });
  };

  const handleStopGatherer = async (gathererId) => {
    setGathererActionInProgress(true);
    setGathererError("");
    try {
      await stopGatherer(token, gathererId);
      await fetchData();
    } catch (err) {
      setGathererError(friendlyMessageForApiError(err));
    } finally {
      setGathererActionInProgress(false);
    }
  };

  const handleDeleteGatherer = (gathererId, gathererName, gatherer = null) => {
    // As with workers, unregistering the service on the host is a separate decision from removing the
    // row here, so it is asked for rather than assumed.
    const serviceBacked = isGathererService(gatherer);
    setConfirmModal({
      message: serviceBacked
        ? t("worker.confirmDeleteWithService", {
            workerName: gathererName,
            hostName: gatherer?.hostName || "",
            defaultValue: `Delete gatherer '${gathererName}'? Its service on ${gatherer?.hostName || "the host"} will also be unregistered. This cannot be undone.`,
          })
        : `Delete gatherer '${gathererName}'? This cannot be undone.`,
      workerName: gathererName,
      icon: "×",
      onConfirm: async () => {
        setConfirmModal(null);
        setGathererActionInProgress(true);
        setGathererError("");
        try {
          await deleteGatherer(token, gathererId, serviceBacked);
          await fetchData();
        } catch (err) {
          setGathererError(friendlyMessageForApiError(err));
        } finally {
          setGathererActionInProgress(false);
        }
      },
    });
  };

  const handleCheckGathererService = async (g) => {
    setGathererActionInProgress(true);
    setGathererError("");
    try {
      const result = await checkGathererService(token, g.id);
      setServiceCheck({ workerName: g.gathererName, ...result });
    } catch (err) {
      setGathererError(friendlyMessageForApiError(err));
    } finally {
      setGathererActionInProgress(false);
    }
  };

  const handleUninstallGathererService = (g) => {
    setConfirmModal({
      message: t("worker.confirmUninstallService", {
        serviceName: g.serviceName || "",
        hostName: g.hostName || "",
        defaultValue: `Unregister service '${g.serviceName}' on ${g.hostName}? The gatherer stays in this list and can be redeployed.`,
      }),
      workerName: g.gathererName,
      icon: "×",
      onConfirm: async () => {
        setConfirmModal(null);
        setGathererActionInProgress(true);
        setGathererError("");
        try {
          await uninstallGathererService(token, g.id);
          await fetchData();
        } catch (err) {
          setGathererError(friendlyMessageForApiError(err));
        } finally {
          setGathererActionInProgress(false);
        }
      },
    });
  };

  const handleOpenGathererSettings = async (g) => {
    setGathererSettingsModal({ id: g.id, name: g.gathererName });
    setGathererSettings(null);
    setGathererSettingsError("");
    setGathererSettingsSaved(false);
    setLoadingGathererSettings(true);
    try {
      const settings = await getGathererAppSettings(token, g.id);
      setGathererSettings(settings);
    } catch (err) {
      setGathererSettingsError(friendlyMessageForApiError(err));
    } finally {
      setLoadingGathererSettings(false);
    }
  };

  const handleCloseGathererSettings = () => {
    setGathererSettingsModal(null);
    setGathererSettings(null);
    setGathererSettingsError("");
    setGathererSettingsSaved(false);
  };

  const handleSaveGathererSettings = async () => {
    if (!gathererSettingsModal || !gathererSettings) return;
    setSavingGathererSettings(true);
    setGathererSettingsError("");
    try {
      await updateGathererAppSettings(token, gathererSettingsModal.id, gathererSettings);
      setGathererSettingsSaved(true);
      setTimeout(() => setGathererSettingsSaved(false), 3000);
    } catch (err) {
      setGathererSettingsError(friendlyMessageForApiError(err));
    } finally {
      setSavingGathererSettings(false);
    }
  };

  const handleOpenGathererLiveLogs = (gatherer) => {
    if (gathererLiveStreamAbortRef.current) gathererLiveStreamAbortRef.current.abort();
    setGathererLiveLines([]);
    setLiveLogsGatherer({ id: gatherer.id, gathererGuid: gatherer.gathererId, name: gatherer.gathererName });
  };

  const handleCloseGathererLiveLogs = () => {
    if (gathererLiveStreamAbortRef.current) gathererLiveStreamAbortRef.current.abort();
    setLiveLogsGatherer(null);
    setGathererLiveLines([]);
  };

  // ── End gatherer handlers ────────────────────────────────────────────────

  // Start/restart SSE stream whenever liveLogsWorker changes
  useEffect(() => {
    if (!liveLogsWorker || !token) return;
    const controller = new AbortController();
    liveStreamAbortRef.current = controller;
    const url = workerLiveLogUrl(liveLogsWorker.workerGuid);

    (async () => {
      try {
        const res = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        });
        if (!res.ok || !res.body) return;
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const parts = buffer.split("\n\n");
          buffer = parts.pop(); // incomplete last chunk
          for (const part of parts) {
            const line = part.trim();
            if (line.startsWith("data: ")) {
              try {
                const text = JSON.parse(line.slice(6));
                setLiveLines((prev) => [...prev.slice(-999), text]);
              } catch {}
            }
          }
        }
      } catch (e) {
        if (e.name !== "AbortError")
          setLiveLines((prev) => [...prev, `[stream error] ${e.message}`]);
      }
    })();

    return () => controller.abort();
  }, [liveLogsWorker, token]);

  // Auto-scroll worker live log terminal to bottom
  useEffect(() => {
    liveLogEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [liveLines]);

  // Start/restart SSE stream whenever liveLogsGatherer changes
  useEffect(() => {
    if (!liveLogsGatherer || !token) return;
    const controller = new AbortController();
    gathererLiveStreamAbortRef.current = controller;
    const url = gathererLiveLogUrl(liveLogsGatherer.gathererGuid);

    (async () => {
      try {
        const res = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        });
        if (!res.ok || !res.body) {
          setGathererLiveLines((prev) => [...prev, `[error] HTTP ${res.status}`]);
          return;
        }
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buf = "";
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });
          const parts = buf.split("\n\n");
          buf = parts.pop();
          for (const part of parts) {
            const dataLine = part.split("\n").find((l) => l.startsWith("data:"));
            if (!dataLine) continue;
            try {
              const text = JSON.parse(dataLine.slice(5).trim());
              setGathererLiveLines((prev) => [...prev.slice(-999), text]);
            } catch {}
          }
        }
      } catch (e) {
        if (e.name !== "AbortError")
          setGathererLiveLines((prev) => [...prev, `[stream error] ${e.message}`]);
      }
    })();

    return () => controller.abort();
  }, [liveLogsGatherer, token]);

  // Auto-scroll gatherer live log terminal to bottom
  useEffect(() => {
    gathererLiveLogEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [gathererLiveLines]);

  return (
    <div className="app-root">
      <div className="nav-strip">
        <NavBar />
      </div>
      <HeaderBar />
      <div className="page-container wm">
        {/* Header */}
        <Hero
          icon={
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9}
              strokeLinecap="round" strokeLinejoin="round" style={{ color: "#fff" }}>
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
            </svg>
          }
          title={t("worker.monitoring", { defaultValue: "Worker Monitoring" })}
          subtitle={t("worker.subtitle", {
            defaultValue: "Real-time monitoring of batch processing workers",
          })}
          onRefresh={fetchData}
          refreshing={loading}
          logo={<PulseArt />}
          actions={
            <>
              <a
                href="/admin/processing-config"
                className="asf-hero__btn"
                title={t("worker.processingConfigLinkTitle", {
                  defaultValue:
                    "Open Processing Configuration to change parallelism, polling interval and logging verbosity",
                })}
              >
                {t("worker.processingConfigLink", {
                  defaultValue: "Processing Configuration",
                })}
              </a>
              <ToggleSwitch
                checked={autoRefresh}
                onChange={setAutoRefresh}
                label={t("worker.autoRefresh", { defaultValue: "Auto-refresh (30s)" })}
              />
            </>
          }
        />

        {error && (
          <div className="hm-alert hm-alert--error">
            {error}
          </div>
        )}

        {/* Worker Status Cards */}
        {workerStatus && (
          <section className="adm-section">
            <h2 className="adm-section__heading">
              {t("worker.statistics", { defaultValue: "Worker Statistics" })}
            </h2>
            <div className="adm-overview-grid">
              {/* Status */}
              <div className="adm-kpi-card">
                <div className="adm-kpi-card__top">
                  <span className="adm-kpi-card__label">
                    {t("worker.status", { defaultValue: "Status" })}
                  </span>
                  <div className="adm-kpi-card__icon adm-kpi-card__icon--green">
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                    </svg>
                  </div>
                </div>
                <div>
                  <span className={`wm-status-badge wm-status-badge--${getStatusClass(workerStatus.status)}`}>
                    {workerStatus.status || t("worker.unknown", { defaultValue: "Unknown" })}
                  </span>
                </div>
              </div>
              {/* Active Workers */}
              <div className="adm-kpi-card">
                <div className="adm-kpi-card__top">
                  <span className="adm-kpi-card__label">
                    {t("worker.activeWorkers", { defaultValue: "Active Workers" })}
                  </span>
                  <div className="adm-kpi-card__icon adm-kpi-card__icon--blue">
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/>
                    </svg>
                  </div>
                </div>
                <div className="adm-kpi-card__value">{workerStatus.activeWorkers || 0}</div>
              </div>
              {/* Processing Now */}
              <div className="adm-kpi-card">
                <div className="adm-kpi-card__top">
                  <span className="adm-kpi-card__label">
                    {t("worker.processingNow", { defaultValue: "Processing Now" })}
                  </span>
                  <div className="adm-kpi-card__icon adm-kpi-card__icon--teal">
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a10 10 0 010 14.14M4.93 4.93a10 10 0 000 14.14"/>
                    </svg>
                  </div>
                </div>
                <div className="adm-kpi-card__value">{workerStatus.currentlyProcessing || 0}</div>
              </div>
              {/* Queue Size */}
              <div className="adm-kpi-card">
                <div className="adm-kpi-card__top">
                  <span className="adm-kpi-card__label">
                    {t("worker.queueSize", { defaultValue: "Queue Size" })}
                  </span>
                  <div className="adm-kpi-card__icon adm-kpi-card__icon--amber">
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/>
                    </svg>
                  </div>
                </div>
                <div className="adm-kpi-card__value">{workerStatus.queueSize || 0}</div>
              </div>
            </div>
          </section>
        )}

        {/* Deployment Settings */}
        <div className="config-card" style={{ marginBottom: 24 }}>
          <div
            style={{
              padding: "14px 24px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              cursor: "pointer",
              userSelect: "none",
            }}
            onClick={() => setShowDeploymentSettings((v) => !v)}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a10 10 0 010 14.14M4.93 4.93a10 10 0 000 14.14"/>
              </svg>
              <h2 style={{ fontSize: 14, fontWeight: 600, margin: 0, color: "#111827" }}>
                {t("worker.deploymentSettings", { defaultValue: "Deployment Settings" })}
              </h2>
              <span className={`wm-pill ${deploymentConfig.enabled ? "wm-pill--enabled" : "wm-pill--disabled"}`}>
                {deploymentConfig.enabled
                  ? t("worker.enabled", { defaultValue: "Enabled" })
                  : t("worker.disabled", { defaultValue: "Disabled" })}
              </span>
              <span className={`wm-pill ${isLocalProcess ? "wm-pill--local" : "wm-pill--docker"}`}>
                {isLocalProcess
                  ? t("worker.localProcess", { defaultValue: "Local Process" })
                  : isServiceMode
                  ? t("worker.nativeService", { defaultValue: "Native Service" })
                  : t("worker.docker", { defaultValue: "Docker" })}
              </span>
            </div>
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              {showDeploymentSettings
                ? <polyline points="18 15 12 9 6 15" />
                : <polyline points="6 9 12 15 18 9" />}
            </svg>
          </div>

          {showDeploymentSettings && (
            <div style={{ padding: "0 24px 24px", borderTop: "1px solid #e5e7eb" }}>
              <div style={{ paddingTop: 20, display: "flex", flexDirection: "column", gap: 20 }}>

                {/* Enable toggle */}
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={deploymentConfig.enabled}
                      onChange={(e) =>
                        setDeploymentConfig({ ...deploymentConfig, enabled: e.target.checked })
                      }
                    />
                    <span style={{ fontSize: 14, fontWeight: 500 }}>
                      Enable worker deployment
                    </span>
                  </label>
                  <span style={{ fontSize: 12, color: "#6b7280" }}>
                    Workers can only be started when deployment is enabled
                  </span>
                </div>

                {/* Mode selection */}
                <div>
                  <p style={{ fontSize: 14, fontWeight: 500, marginBottom: 10 }}>
                    Deployment Mode
                  </p>
                  <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                    <label
                      style={{
                        flex: "1 1 220px",
                        padding: 16,
                        border: isLocalProcess ? "2px solid #3b82f6" : "2px solid #d1d5db",
                        borderRadius: 8,
                        cursor: "pointer",
                        backgroundColor: isLocalProcess ? "#eff6ff" : "#fff",
                      }}
                    >
                      <div style={{ display: "flex", gap: 10 }}>
                        <input
                          type="radio"
                          checked={isLocalProcess}
                          onChange={() =>
                            setDeploymentConfig({ ...deploymentConfig, mode: "LocalProcess" })
                          }
                        />
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 14 }}>Local Process</div>
                          <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
                            Run the worker as a .NET process on this machine. No Docker required.
                          </div>
                        </div>
                      </div>
                    </label>

                    <label
                      style={{
                        flex: "1 1 220px",
                        padding: 16,
                        border: isServiceMode ? "2px solid #3b82f6" : "2px solid #d1d5db",
                        borderRadius: 8,
                        cursor: "pointer",
                        backgroundColor: isServiceMode ? "#eff6ff" : "#fff",
                      }}
                    >
                      <div style={{ display: "flex", gap: 10 }}>
                        <input
                          type="radio"
                          checked={isServiceMode}
                          onChange={() =>
                            setDeploymentConfig({ ...deploymentConfig, mode: "Service" })
                          }
                        />
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 14 }}>
                            {t("worker.nativeService", { defaultValue: "Native Service" })}
                          </div>
                          <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
                            {t("worker.nativeServiceHelp", {
                              defaultValue:
                                "Run the worker as a Windows service or systemd unit on this or a remote host, over SSH. The Depository installer must already have been run there.",
                            })}
                          </div>
                        </div>
                      </div>
                    </label>

                    <label
                      style={{
                        flex: "1 1 220px",
                        padding: 16,
                        border: !isLocalProcess && !isServiceMode ? "2px solid #3b82f6" : "2px solid #d1d5db",
                        borderRadius: 8,
                        cursor: "pointer",
                        backgroundColor: !isLocalProcess && !isServiceMode ? "#eff6ff" : "#fff",
                      }}
                    >
                      <div style={{ display: "flex", gap: 10 }}>
                        <input
                          type="radio"
                          checked={!isLocalProcess && !isServiceMode}
                          onChange={() =>
                            setDeploymentConfig({ ...deploymentConfig, mode: "Docker" })
                          }
                        />
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 14 }}>Docker</div>
                          <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
                            Run the worker in a Docker container (local or remote host via SSH).
                          </div>
                        </div>
                      </div>
                    </label>
                  </div>

                  {isServiceMode && (
                    <p style={{ fontSize: 12, color: "#6b7280", marginTop: 10 }}>
                      {t("worker.nativeServiceNote", {
                        defaultValue:
                          "Each host needs its install directory set under Admin -> Hosts. A Windows target also needs the OpenSSH Server feature, with an SSH user who can manage services; a Linux target needs passwordless sudo for systemctl.",
                      })}
                    </p>
                  )}
                </div>

                {/* Local worker path (optional, LocalProcess only) */}
                {isLocalProcess && (
                  <div>
                    <label style={{ display: "block", fontSize: 14, fontWeight: 500, marginBottom: 6 }}>
                      Worker Binary Path{" "}
                      <span style={{ fontWeight: 400, color: "#6b7280" }}>(optional — auto-detected if blank)</span>
                    </label>
                    <input
                      type="text"
                      value={deploymentConfig.localWorkerPath}
                      onChange={(e) =>
                        setDeploymentConfig({ ...deploymentConfig, localWorkerPath: e.target.value })
                      }
                      placeholder="/path/to/DEPOSITORY.CORE.WORKER.dll"
                      style={{
                        width: "100%",
                        padding: "8px 12px",
                        border: "1px solid #d1d5db",
                        borderRadius: 6,
                        fontFamily: "monospace",
                        fontSize: 13,
                      }}
                    />
                    <p style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
                      Auto-detection checks Debug and Release build outputs. Build DEPOSITORY.CORE.WORKER before starting.
                    </p>
                  </div>
                )}

                {/* Save */}
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <button
                    onClick={handleSaveDeploymentConfig}
                    disabled={savingDeploymentConfig}
                    className="go-btn"
                    style={{ minWidth: 140 }}
                  >
                    {savingDeploymentConfig ? "Saving..." : "Save Settings"}
                  </button>
                  {deploymentConfigSaved && (
                    <span style={{ fontSize: 13, color: "#16a34a" }}>
                      ✓ Saved
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Worker Instances Management */}
        {workerStatus && (
          <div className="config-card" style={{ marginBottom: 24 }}>
            <div
              style={{
                padding: "14px 24px",
                borderBottom: "1px solid #e5e7eb",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <h2 style={{ fontSize: 14, fontWeight: 600, margin: 0, color: "#111827" }}>
                {t("worker.workerInstances", { defaultValue: "Worker Instances" })}
              </h2>
              <button
                onClick={() => setShowCreateWorker(!showCreateWorker)}
                className="adm-btn adm-btn--outline"
              >
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                {t("worker.addNewWorker", { defaultValue: "Add New Worker" })}
              </button>
            </div>

            {showCreateWorker && (
              <div
                style={{
                  padding: 24,
                  backgroundColor: "#f9fafb",
                  borderBottom: "1px solid #e5e7eb",
                }}
              >
                <div
                  style={{ display: "flex", gap: 12, alignItems: "flex-end" }}
                >
                  <div style={{ flex: 1 }}>
                    <label
                      style={{
                        display: "block",
                        fontSize: 14,
                        marginBottom: 6,
                        fontWeight: 500,
                      }}
                    >
                      Worker Name
                    </label>
                    <input
                      type="text"
                      value={newWorkerName}
                      onChange={(e) => setNewWorkerName(e.target.value)}
                      placeholder={t("worker.workerNamePlaceholder", {
                        defaultValue: "Enter worker name...",
                      })}
                      style={{
                        width: "100%",
                        padding: "8px 12px",
                        border: "1px solid #d1d5db",
                        borderRadius: 6,
                        fontSize: 14,
                      }}
                      onKeyPress={(e) => {
                        if (e.key === "Enter") handleCreateWorker();
                      }}
                    />
                  </div>
                  <button
                    onClick={handleCreateWorker}
                    disabled={actionInProgress || !newWorkerName.trim()}
                    className="go-btn"
                    style={{ minWidth: 100 }}
                  >
                    {actionInProgress
                      ? t("worker.creating", { defaultValue: "Creating..." })
                      : t("worker.create", { defaultValue: "Create" })}
                  </button>
                  <button
                    onClick={() => {
                      setShowCreateWorker(false);
                      setNewWorkerName("");
                    }}
                    className="cancel-btn"
                    style={{ minWidth: 100 }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            <div style={{ padding: 24 }}>
              {workerStatus.workerInstances &&
              workerStatus.workerInstances.length > 0 ? (
                <div>
                  {/* Group workers by host */}
                  {(() => {
                    // Group workers by hostname
                    const workersByHost = {};
                    workerStatus.workerInstances.forEach((worker) => {
                      const hostName =
                        worker.hostName ||
                        t("worker.unknownHost", {
                          defaultValue: "Unknown Host",
                        });
                      if (!workersByHost[hostName]) {
                        workersByHost[hostName] = [];
                      }
                      workersByHost[hostName].push(worker);
                    });

                    // Find host details for each hostname
                    const getHostDetails = (hostName) => {
                      return hosts.find((h) => h.hostName === hostName);
                    };

                    return Object.entries(workersByHost).map(
                      ([hostName, hostWorkers]) => {
                        const hostDetails = getHostDetails(hostName);
                        const isLocal = hostDetails?.isLocal || false;
                        const healthStatus =
                          hostDetails?.healthStatus ||
                          t("worker.unknown", { defaultValue: "Unknown" });

                        return (
                          <div
                            key={hostName}
                            className={`wm-host-card${isLocal ? " wm-host-card--local" : ""}`}
                          >
                            {/* Host Header */}
                            <div className="wm-host-card__header">
                              <div className="wm-host-card__left">
                                <div className={`wm-host-icon${isLocal ? "" : " wm-host-icon--remote"}`}>
                                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    {isLocal
                                      ? <><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></>
                                      : <><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/></>}
                                  </svg>
                                </div>
                                <div>
                                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                    <p className="wm-host-card__name">
                                      {hostDetails?.displayName || hostName}
                                    </p>
                                    {isLocal && (
                                      <span className="wm-pill wm-pill--local" style={{ fontSize: 10 }}>LOCAL</span>
                                    )}
                                  </div>
                                  <p className="wm-host-card__meta">
                                    {hostDetails?.address || hostName}
                                    {hostDetails && ` · ${hostDetails.cpuCores} CPU · ${hostDetails.memoryGB} GB RAM`}
                                    {` · Health: ${healthStatus}`}
                                  </p>
                                </div>
                              </div>
                              <div className="wm-host-card__right">
                                <div>Running: <strong>{hostWorkers.filter((w) => w.state === "Running").length}</strong></div>
                                <div>Stopped: <strong>{hostWorkers.filter((w) => w.state === "Stopped").length}</strong></div>
                              </div>
                            </div>

                            {/* Workers Grid */}
                            <div className="wm-host-card__body">
                              {hostWorkers.map((worker) => (
                                <div key={worker.id} className="wm-worker-card">
                                  {/* Worker Header */}
                                  <div className="wm-worker-card__header">
                                    {/* Left: name + status */}
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                      <div className="wm-worker-card__name-row">
                                        <p className="wm-worker-card__name">{worker.workerName}</p>
                                        <span className={`wm-status-badge wm-status-badge--${getStatusClass(worker.state)}`}>
                                          {worker.state}
                                        </span>
                                      </div>
                                      <p className="wm-worker-card__id">{worker.workerId.substring(0, 12)}…</p>
                                      {worker.processId && (
                                        <p className="wm-worker-card__pid">PID {worker.processId}</p>
                                      )}
                                      {worker.containerId && (
                                        <p className="wm-worker-card__pid" style={{ fontFamily: "monospace" }}>
                                          {worker.containerId.substring(0, 12)}
                                        </p>
                                      )}
                                    </div>
                                    {/* Right: deploy type */}
                                    {worker.deploymentType && (
                                      <span
                                        className={`wm-deploy-badge wm-deploy-badge--${worker.deploymentType?.toLowerCase() === "docker" ? "docker" : "local"}`}
                                        title={
                                          worker.processId ? `PID: ${worker.processId}`
                                          : worker.containerId ? `Container: ${worker.containerId}`
                                          : worker.deploymentType
                                        }
                                      >
                                        {worker.deploymentType?.toLowerCase() === "docker" ? "DOCKER" : "LOCAL"}
                                      </span>
                                    )}
                                  </div>

                                  {/* Worker Stats */}
                                  <div className="wm-worker-stats">
                                    <div>
                                      <p className="wm-worker-stats__label">{t("worker.current", { defaultValue: "Current" })}</p>
                                      <p className="wm-worker-stats__value wm-worker-stats__value--blue">{worker.currentBatchCount}</p>
                                    </div>
                                    <div>
                                      <p className="wm-worker-stats__label">{t("worker.processed", { defaultValue: "Processed" })}</p>
                                      <p className="wm-worker-stats__value wm-worker-stats__value--green">{worker.totalBatchesProcessed}</p>
                                    </div>
                                    <div>
                                      <p className="wm-worker-stats__label">{t("worker.failed", { defaultValue: "Failed" })}</p>
                                      <p className="wm-worker-stats__value wm-worker-stats__value--red">{worker.totalBatchesFailed}</p>
                                    </div>
                                  </div>

                                  {/* Heartbeat */}
                                  <p className="wm-heartbeat">
                                    <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                                    </svg>
                                    {worker.lastHeartbeat
                                      ? new Date(worker.lastHeartbeat).toLocaleString()
                                      : t("worker.never", { defaultValue: "Never" })}
                                  </p>

                                  {/* Actions: primary labeled + secondary icon-only */}
                                  <div className="wm-actions">
                                    {/* Primary state actions */}
                                    <div className="wm-actions__primary">
                                      {worker.state === "Stopped" && (
                                        <button
                                          className="wm-act-btn wm-act-btn--start"
                                          onClick={() => handleStartWorker(worker.id, worker.workerName)}
                                          disabled={actionInProgress}
                                          title={t("worker.startTitle", { defaultValue: "Start worker" })}
                                        >
                                          <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                                        </button>
                                      )}
                                      {worker.state === "Running" && (
                                        <>
                                          <button
                                            className="wm-act-btn wm-act-btn--pause"
                                            onClick={() => handlePauseWorker(worker.id, worker.workerName)}
                                            disabled={actionInProgress}
                                            title={t("worker.pauseTitle", { defaultValue: "Pause worker" })}
                                          >
                                            <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
                                          </button>
                                          <button
                                            className="wm-act-btn wm-act-btn--stop"
                                            onClick={() => handleStopWorker(worker.id, worker.workerName)}
                                            disabled={actionInProgress}
                                            title={t("worker.stopTitle", { defaultValue: "Stop worker" })}
                                          >
                                            <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="4" width="16" height="16" rx="2"/></svg>
                                          </button>
                                        </>
                                      )}
                                      {worker.state === "Paused" && (
                                        <>
                                          <button
                                            className="wm-act-btn wm-act-btn--start"
                                            onClick={() => handleStartWorker(worker.id, worker.workerName)}
                                            disabled={actionInProgress}
                                            title={t("worker.resumeTitle", { defaultValue: "Resume worker" })}
                                          >
                                            <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                                          </button>
                                          <button
                                            className="wm-act-btn wm-act-btn--stop"
                                            onClick={() => handleStopWorker(worker.id, worker.workerName)}
                                            disabled={actionInProgress}
                                            title={t("worker.stopTitle", { defaultValue: "Stop worker" })}
                                          >
                                            <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="4" width="16" height="16" rx="2"/></svg>
                                          </button>
                                        </>
                                      )}
                                      {(worker.state === "Running" || worker.state === "Paused") && (
                                        <button
                                          className="wm-act-btn wm-act-btn--neutral"
                                          onClick={() => handleMakeOffline(worker.id, worker.workerName)}
                                          disabled={actionInProgress}
                                          title={t("worker.offlineTitle", { defaultValue: "Make offline" })}
                                        >
                                          <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="1" y1="1" x2="23" y2="23"/><path d="M16.72 11.06A10.94 10.94 0 0119 12.55M5 12.55a10.94 10.94 0 015.17-2.39M10.71 5.05A16 16 0 0122.56 9M1.42 9a15.91 15.91 0 014.7-2.88M8.53 16.11a6 6 0 016.95 0"/><line x1="12" y1="20" x2="12.01" y2="20"/></svg>
                                        </button>
                                      )}
                                    </div>
                                    {/* Secondary icon-only actions */}
                                    <div className="wm-actions__secondary">
                                      <button
                                        className="wm-icon-btn"
                                        onClick={() => handleViewLogs(worker.id)}
                                        disabled={actionInProgress}
                                        title={t("worker.viewLogsTitle", { defaultValue: "View audit logs" })}
                                      >
                                        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                          <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
                                        </svg>
                                      </button>
                                      <button
                                        className="wm-icon-btn"
                                        onClick={() => handleOpenWorkerSettings(worker)}
                                        disabled={actionInProgress}
                                        title={t("worker.settingsTitle", { defaultValue: "Edit worker settings" })}
                                      >
                                        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                          <circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a10 10 0 010 14.14M4.93 4.93a10 10 0 000 14.14"/>
                                        </svg>
                                      </button>
                                      {worker.state === "Running" && worker.deploymentType?.toLowerCase() !== "docker" && (
                                        <button
                                          className="wm-icon-btn wm-icon-btn--terminal"
                                          onClick={() => handleOpenLiveLogs(worker)}
                                          disabled={actionInProgress}
                                          title={t("worker.liveLogsTitle", { defaultValue: "View live logs" })}
                                        >
                                          &gt;_
                                        </button>
                                      )}
                                      {isServiceWorker(worker) && (
                                        <button
                                          className="wm-icon-btn"
                                          onClick={() => handleCheckService(worker)}
                                          disabled={actionInProgress}
                                          title={t("worker.checkServiceTitle", {
                                            defaultValue: "Ask the host's service manager about this worker",
                                          })}
                                        >
                                          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                                          </svg>
                                        </button>
                                      )}
                                      {isServiceWorker(worker) && worker.state === "Stopped" && (
                                        <button
                                          className="wm-icon-btn wm-icon-btn--danger"
                                          onClick={() => handleUninstallService(worker)}
                                          disabled={actionInProgress}
                                          title={t("worker.uninstallServiceTitle", {
                                            defaultValue: "Unregister this worker's service on its host",
                                          })}
                                        >
                                          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M18.36 6.64A9 9 0 1 1 5.64 6.64"/><line x1="12" y1="2" x2="12" y2="12"/>
                                          </svg>
                                        </button>
                                      )}
                                      {worker.state === "Stopped" && (
                                        <button
                                          className="wm-icon-btn wm-icon-btn--danger"
                                          onClick={() => handleDeleteWorker(worker.id, worker.workerName, worker)}
                                          disabled={actionInProgress}
                                          title={t("worker.deleteTitle", { defaultValue: "Delete worker" })}
                                        >
                                          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/>
                                          </svg>
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      }
                    );
                  })()}
                </div>
              ) : (
                <div className="wm-empty">
                  <svg viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16"/><circle cx="9" cy="12" r="1"/><circle cx="15" cy="12" r="1"/>
                  </svg>
                  <p className="wm-empty__title">{t("worker.noInstances", { defaultValue: "No worker instances registered" })}</p>
                  <p className="wm-empty__sub">{t("worker.addWorkerHint", { defaultValue: "Click \"Add New Worker\" to create one" })}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Gatherer Deployment Settings ────────────────────────────── */}
        <div className="config-card" style={{ marginBottom: 24 }}>
          <div
            style={{
              padding: "16px 24px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              cursor: "pointer",
              userSelect: "none",
            }}
            onClick={() => setShowGathererDeploymentSettings((v) => !v)}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/>
              </svg>
              <h2 style={{ fontSize: 14, fontWeight: 600, margin: 0, color: "#111827" }}>
                {t("worker.gathererDeploymentSettings", { defaultValue: "Gatherer Deployment Settings" })}
              </h2>
              <span className={`wm-pill ${gathererDeploymentConfig.enabled ? "wm-pill--enabled" : "wm-pill--disabled"}`}>
                {gathererDeploymentConfig.enabled
                  ? t("worker.enabled", { defaultValue: "Enabled" })
                  : t("worker.disabled", { defaultValue: "Disabled" })}
              </span>
              <span className={`wm-pill ${isGathererLocalProcess ? "wm-pill--local" : "wm-pill--docker"}`}>
                {isGathererServiceMode
                  ? t("worker.nativeService", { defaultValue: "Native Service" })
                  : isGathererLocalProcess
                  ? t("worker.localProcess", { defaultValue: "Local Process" })
                  : t("worker.docker", { defaultValue: "Docker" })}
              </span>
            </div>
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              {showGathererDeploymentSettings
                ? <polyline points="18 15 12 9 6 15" />
                : <polyline points="6 9 12 15 18 9" />}
            </svg>
          </div>

          {showGathererDeploymentSettings && (
            <div style={{ padding: "0 24px 24px", borderTop: "1px solid #e5e7eb" }}>
              <div style={{ paddingTop: 20, display: "flex", flexDirection: "column", gap: 20 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={gathererDeploymentConfig.enabled}
                      onChange={(e) =>
                        setGathererDeploymentConfig({ ...gathererDeploymentConfig, enabled: e.target.checked })
                      }
                    />
                    <span style={{ fontSize: 14, fontWeight: 500 }}>Enable gatherer deployment</span>
                  </label>
                  <span style={{ fontSize: 12, color: "#6b7280" }}>
                    Gatherers can only be started when deployment is enabled
                  </span>
                </div>

                <div>
                  <p style={{ fontSize: 14, fontWeight: 500, marginBottom: 10 }}>Deployment Mode</p>
                  <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                    <label
                      style={{
                        flex: "1 1 220px", padding: 16,
                        border: isGathererLocalProcess ? "2px solid #3b82f6" : "2px solid #d1d5db",
                        borderRadius: 8, cursor: "pointer",
                        backgroundColor: isGathererLocalProcess ? "#eff6ff" : "#fff",
                      }}
                    >
                      <div style={{ display: "flex", gap: 10 }}>
                        <input
                          type="radio"
                          checked={isGathererLocalProcess}
                          onChange={() => setGathererDeploymentConfig({ ...gathererDeploymentConfig, mode: "LocalProcess" })}
                        />
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 14 }}>Local Process</div>
                          <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
                            Run the gatherer as a .NET process on this machine.
                          </div>
                        </div>
                      </div>
                    </label>
                    <label
                      style={{
                        flex: "1 1 220px", padding: 16,
                        border: isGathererServiceMode ? "2px solid #3b82f6" : "2px solid #d1d5db",
                        borderRadius: 8, cursor: "pointer",
                        backgroundColor: isGathererServiceMode ? "#eff6ff" : "#fff",
                      }}
                    >
                      <div style={{ display: "flex", gap: 10 }}>
                        <input
                          type="radio"
                          checked={isGathererServiceMode}
                          onChange={() => setGathererDeploymentConfig({ ...gathererDeploymentConfig, mode: "Service" })}
                        />
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 14 }}>
                            {t("worker.nativeService", { defaultValue: "Native Service" })}
                          </div>
                          <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
                            {t("worker.gathererNativeServiceHelp", {
                              defaultValue:
                                "Run the gatherer as a Windows service or systemd unit on this or a remote host, over SSH. This is the only mode that can place a gatherer on another machine.",
                            })}
                          </div>
                        </div>
                      </div>
                    </label>
                    <label
                      style={{
                        flex: "1 1 220px", padding: 16,
                        border: !isGathererLocalProcess && !isGathererServiceMode ? "2px solid #3b82f6" : "2px solid #d1d5db",
                        borderRadius: 8, cursor: "pointer",
                        backgroundColor: !isGathererLocalProcess && !isGathererServiceMode ? "#eff6ff" : "#fff",
                      }}
                    >
                      <div style={{ display: "flex", gap: 10 }}>
                        <input
                          type="radio"
                          checked={!isGathererLocalProcess && !isGathererServiceMode}
                          onChange={() => setGathererDeploymentConfig({ ...gathererDeploymentConfig, mode: "Docker" })}
                        />
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 14 }}>Docker</div>
                          <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
                            {t("worker.gathererDockerHelp", {
                              defaultValue:
                                "Run the gatherer in a Docker container on this machine. Unlike workers, gatherer containers are not deployed to remote hosts.",
                            })}
                          </div>
                        </div>
                      </div>
                    </label>
                  </div>

                  {isGathererServiceMode && (
                    <p style={{ fontSize: 12, color: "#6b7280", marginTop: 10 }}>
                      {t("worker.gathererNativeServiceNote", {
                        defaultValue:
                          "The gatherer listens on its configured port, and its service is registered bound to that port. Opening the host firewall for it stays a decision you make on the host.",
                      })}
                    </p>
                  )}
                </div>

                {isGathererLocalProcess && (
                  <div>
                    <label style={{ display: "block", fontSize: 14, fontWeight: 500, marginBottom: 6 }}>
                      Gatherer Binary Path{" "}
                      <span style={{ fontWeight: 400, color: "#6b7280" }}>(optional — auto-detected if blank)</span>
                    </label>
                    <input
                      type="text"
                      value={gathererDeploymentConfig.localGathererPath}
                      onChange={(e) =>
                        setGathererDeploymentConfig({ ...gathererDeploymentConfig, localGathererPath: e.target.value })
                      }
                      placeholder="/path/to/DEPOSITORY.CORE.GATHERER.dll"
                      style={{
                        width: "100%", padding: "8px 12px",
                        border: "1px solid #d1d5db", borderRadius: 6,
                        fontFamily: "monospace", fontSize: 13,
                      }}
                    />
                    <p style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
                      Auto-detection checks Debug and Release build outputs. Build DEPOSITORY.CORE.GATHERER before starting.
                    </p>
                  </div>
                )}

                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <button
                    onClick={handleSaveGathererConfig}
                    disabled={savingGathererConfig}
                    className="go-btn"
                    style={{ minWidth: 140 }}
                  >
                    {savingGathererConfig ? "Saving..." : "Save Settings"}
                  </button>
                  {gathererConfigSaved && (
                    <span style={{ fontSize: 13, color: "#16a34a" }}>✓ Saved</span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── Gatherer Instances ─────────────────────────────────────── */}
        <div className="config-card" style={{ marginBottom: 24 }}>
          <div
            style={{
              padding: "20px 24px",
              borderBottom: "1px solid #e5e7eb",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div>
              <h2 style={{ fontSize: 14, fontWeight: 600, margin: 0, color: "#111827" }}>
                {t("worker.gathererInstances", { defaultValue: "Gatherer Instances" })}
              </h2>
              {gathererStatus && (
                <p style={{ fontSize: 12, color: "#6b7280", margin: "3px 0 0" }}>
                  {gathererStatus.activeGatherers || 0} active ·{" "}
                  <span className={`wm-status-badge wm-status-badge--${getStatusClass(gathererStatus.status)}`} style={{ fontSize: 11, padding: "1px 8px" }}>
                    {gathererStatus.status || t("worker.unknown", { defaultValue: "Unknown" })}
                  </span>
                </p>
              )}
            </div>
            <button
              onClick={() => setShowCreateGatherer(!showCreateGatherer)}
              className="adm-btn adm-btn--outline"
            >
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              {t("worker.addNewGatherer", { defaultValue: "Add New Gatherer" })}
            </button>
          </div>

          {showCreateGatherer && (
            <div style={{ padding: 24, backgroundColor: "#f9fafb", borderBottom: "1px solid #e5e7eb" }}>
              <div style={{ display: "flex", gap: 12, alignItems: "flex-end" }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: "block", fontSize: 14, marginBottom: 6, fontWeight: 500 }}>
                    Gatherer Name
                  </label>
                  <input
                    type="text"
                    value={newGathererName}
                    onChange={(e) => setNewGathererName(e.target.value)}
                    placeholder="Enter gatherer name..."
                    style={{ width: "100%", padding: "8px 12px", border: "1px solid #d1d5db", borderRadius: 6, fontSize: 14 }}
                    onKeyPress={(e) => { if (e.key === "Enter") handleCreateGatherer(); }}
                  />
                </div>
                <button
                  onClick={handleCreateGatherer}
                  disabled={gathererActionInProgress || !newGathererName.trim()}
                  className="go-btn"
                  style={{ minWidth: 100 }}
                >
                  {gathererActionInProgress ? "Creating..." : "Create"}
                </button>
                <button
                  onClick={() => { setShowCreateGatherer(false); setNewGathererName(""); }}
                  className="cancel-btn"
                  style={{ minWidth: 100 }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {gathererError && (
            <div
              style={{
                margin: "0 24px",
                padding: "12px 16px",
                backgroundColor: "#fef2f2",
                border: "1px solid #fecaca",
                borderRadius: 6,
                color: "#991b1b",
                fontSize: 14,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span>{gathererError}</span>
              <button
                onClick={() => setGathererError("")}
                style={{ background: "none", border: "none", cursor: "pointer", color: "#991b1b", fontSize: 16, padding: "0 4px" }}
              >
                ×
              </button>
            </div>
          )}

          <div style={{ padding: 24 }}>
            {gathererStatus?.gathererInstances?.length > 0 ? (
              <div>
                {(() => {
                  const gatherersByHost = {};
                  gathererStatus.gathererInstances.forEach((g) => {
                    const hostName = g.hostName || "Unknown Host";
                    if (!gatherersByHost[hostName]) gatherersByHost[hostName] = [];
                    gatherersByHost[hostName].push(g);
                  });

                  return Object.entries(gatherersByHost).map(([hostName, hostGatherers]) => {
                    const hostDetails = hosts.find((h) => h.hostName === hostName);
                    const isLocal = hostDetails?.isLocal || hostName === (window.location.hostname || "localhost");

                    return (
                      <div
                        key={hostName}
                        className={`wm-host-card${isLocal ? " wm-host-card--local" : ""}`}
                      >
                        <div className="wm-host-card__header">
                          <div className="wm-host-card__left">
                            <div className={`wm-host-icon${isLocal ? "" : " wm-host-icon--remote"}`}>
                              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                {isLocal
                                  ? <><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></>
                                  : <><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/></>}
                              </svg>
                            </div>
                            <div>
                              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <p className="wm-host-card__name">{hostDetails?.displayName || hostName}</p>
                                {isLocal && <span className="wm-pill wm-pill--local" style={{ fontSize: 10 }}>LOCAL</span>}
                              </div>
                              <p className="wm-host-card__meta">
                                {hostGatherers.length} gatherer{hostGatherers.length !== 1 ? "s" : ""}
                              </p>
                            </div>
                          </div>
                          <div className="wm-host-card__right">
                            <div>Running: <strong>{hostGatherers.filter((g) => g.state === "Running").length}</strong></div>
                            <div>Stopped: <strong>{hostGatherers.filter((g) => g.state === "Stopped").length}</strong></div>
                          </div>
                        </div>

                        <div className="wm-host-card__body">
                          {hostGatherers.map((g) => (
                            <div key={g.id} className="wm-worker-card">
                              <div className="wm-worker-card__header">
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div className="wm-worker-card__name-row">
                                    <p className="wm-worker-card__name">{g.gathererName}</p>
                                    <span className={`wm-status-badge wm-status-badge--${getStatusClass(g.state)}`}>{g.state}</span>
                                  </div>
                                  <p className="wm-worker-card__id">{g.gathererId?.substring(0, 12)}…</p>
                                  {g.processId && <p className="wm-worker-card__pid">PID {g.processId}</p>}
                                  {g.servicePort && <p className="wm-worker-card__pid">Port {g.servicePort}</p>}
                                </div>
                                {g.deploymentType && (
                                  <span className={`wm-deploy-badge wm-deploy-badge--${g.deploymentType?.toLowerCase() === "docker" ? "docker" : "local"}`}>
                                    {g.deploymentType?.toLowerCase() === "docker" ? "DOCKER" : "LOCAL"}
                                  </span>
                                )}
                              </div>

                              {g.lastError && (
                                <div style={{ fontSize: 11, color: "#991b1b", background: "#fef2f2", padding: "6px 8px", borderRadius: 4, marginBottom: 10, wordBreak: "break-all" }}>
                                  {g.lastError.length > 120 ? g.lastError.substring(0, 120) + "…" : g.lastError}
                                </div>
                              )}

                              <div className="wm-actions">
                                <div className="wm-actions__primary">
                                  {g.state === "Stopped" && (
                                    <button
                                      className={`wm-act-btn ${gathererDeploymentConfig.enabled ? "wm-act-btn--start" : "wm-act-btn--neutral"}`}
                                      onClick={() => handleStartGatherer(g.id, g.gathererName)}
                                      disabled={gathererActionInProgress}
                                      title={!gathererDeploymentConfig.enabled ? "Deployment is disabled" : "Start gatherer"}
                                    >
                                      <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                                    </button>
                                  )}
                                  {g.state === "Running" && (
                                    <button
                                      className="wm-act-btn wm-act-btn--stop"
                                      onClick={() => handleStopGatherer(g.id)}
                                      disabled={gathererActionInProgress}
                                      title={t("worker.stopTitle", { defaultValue: "Stop gatherer" })}
                                    >
                                      <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="4" width="16" height="16" rx="2"/></svg>
                                    </button>
                                  )}
                                </div>
                                <div className="wm-actions__secondary">
                                  <button
                                    className="wm-icon-btn"
                                    onClick={() => handleOpenGathererSettings(g)}
                                    disabled={gathererActionInProgress}
                                    title={t("worker.settingsTitle", { defaultValue: "Edit gatherer settings" })}
                                  >
                                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                      <circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a10 10 0 010 14.14M4.93 4.93a10 10 0 000 14.14"/>
                                    </svg>
                                  </button>
                                  {g.state === "Running" && g.deploymentType?.toLowerCase() !== "docker" && (
                                    <button
                                      className="wm-icon-btn wm-icon-btn--terminal"
                                      onClick={() => handleOpenGathererLiveLogs(g)}
                                      disabled={gathererActionInProgress}
                                      title={t("worker.liveLogsTitle", { defaultValue: "View live logs" })}
                                    >
                                      &gt;_
                                    </button>
                                  )}
                                  {isGathererService(g) && (
                                    <button
                                      className="wm-icon-btn"
                                      onClick={() => handleCheckGathererService(g)}
                                      disabled={gathererActionInProgress}
                                      title={t("worker.checkServiceTitle", {
                                        defaultValue: "Ask the host's service manager about this worker",
                                      })}
                                    >
                                      <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                                      </svg>
                                    </button>
                                  )}
                                  {isGathererService(g) && g.state === "Stopped" && (
                                    <button
                                      className="wm-icon-btn wm-icon-btn--danger"
                                      onClick={() => handleUninstallGathererService(g)}
                                      disabled={gathererActionInProgress}
                                      title={t("worker.uninstallServiceTitle", {
                                        defaultValue: "Unregister this worker's service on its host",
                                      })}
                                    >
                                      <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M18.36 6.64A9 9 0 1 1 5.64 6.64"/><line x1="12" y1="2" x2="12" y2="12"/>
                                      </svg>
                                    </button>
                                  )}
                                  <button
                                    className="wm-icon-btn wm-icon-btn--danger"
                                    onClick={() => handleDeleteGatherer(g.id, g.gathererName, g)}
                                    disabled={gathererActionInProgress}
                                    title={t("worker.deleteTitle", { defaultValue: "Delete gatherer" })}
                                  >
                                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                      <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/>
                                    </svg>
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            ) : (
              <div className="wm-empty">
                <svg viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/>
                </svg>
                <p className="wm-empty__title">{t("worker.noGatherers", { defaultValue: "No gatherer instances registered" })}</p>
                <p className="wm-empty__sub">{t("worker.addGathererHint", { defaultValue: "Click \"Add New Gatherer\" to create one" })}</p>
              </div>
            )}
          </div>
        </div>

        {/* Batch Processing Statistics */}
        {batchStats && (
          <div className="config-card" style={{ marginBottom: 24 }}>
            <div style={{ padding: "14px 24px", borderBottom: "1px solid #e5e7eb" }}>
              <h2 style={{ fontSize: 14, fontWeight: 600, margin: 0, color: "#111827" }}>
                {t("worker.batchStats", { defaultValue: "Batch Processing Statistics" })}
              </h2>
            </div>
            <div style={{ padding: 24 }}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                  gap: 24,
                }}
              >
                <div>
                  <p style={{ fontSize: 13, color: "#666", marginBottom: 6 }}>
                    Total Batches
                  </p>
                  <p style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>
                    {batchStats.totalBatches || 0}
                  </p>
                </div>
                <div>
                  <p style={{ fontSize: 13, color: "#666", marginBottom: 6 }}>
                    Completed
                  </p>
                  <p
                    style={{
                      fontSize: 24,
                      fontWeight: 700,
                      margin: 0,
                      color: "#16a34a",
                    }}
                  >
                    {batchStats.completed || 0}
                  </p>
                </div>
                <div>
                  <p style={{ fontSize: 13, color: "#666", marginBottom: 6 }}>
                    Failed
                  </p>
                  <p
                    style={{
                      fontSize: 24,
                      fontWeight: 700,
                      margin: 0,
                      color: "#dc2626",
                    }}
                  >
                    {batchStats.failed || 0}
                  </p>
                </div>
                <div>
                  <p style={{ fontSize: 13, color: "#666", marginBottom: 6 }}>
                    Success Rate
                  </p>
                  <p
                    style={{
                      fontSize: 24,
                      fontWeight: 700,
                      margin: 0,
                      color: "#2563eb",
                    }}
                  >
                    {batchStats.successRate
                      ? `${batchStats.successRate.toFixed(1)}%`
                      : t("worker.na", { defaultValue: "N/A" })}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Performance Metrics */}
        {metrics && (
          <div className="config-card">
            <div
              style={{
                padding: "20px 24px",
                borderBottom: "1px solid #e5e7eb",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <h2 style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>
                Performance Metrics
              </h2>
              <select
                value={timeRange}
                onChange={(e) => setTimeRange(Number(e.target.value))}
                style={{
                  padding: "6px 12px",
                  border: "1px solid #d1d5db",
                  borderRadius: 6,
                  fontSize: 14,
                }}
              >
                <option value={15}>Last 15 minutes</option>
                <option value={60}>Last hour</option>
                <option value={240}>Last 4 hours</option>
                <option value={1440}>Last 24 hours</option>
              </select>
            </div>
            <div style={{ padding: 24 }}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                  gap: 24,
                }}
              >
                <div
                  style={{
                    borderLeft: "4px solid #3b82f6",
                    paddingLeft: 16,
                  }}
                >
                  <p style={{ fontSize: 13, color: "#666", marginBottom: 6 }}>
                    Avg Processing Time
                  </p>
                  <p style={{ fontSize: 20, fontWeight: 600, margin: 0 }}>
                    {metrics.avgProcessingTime
                      ? `${metrics.avgProcessingTime.toFixed(2)}s`
                      : t("worker.na", { defaultValue: "N/A" })}
                  </p>
                </div>
                <div
                  style={{
                    borderLeft: "4px solid #22c55e",
                    paddingLeft: 16,
                  }}
                >
                  <p style={{ fontSize: 13, color: "#666", marginBottom: 6 }}>
                    Throughput
                  </p>
                  <p style={{ fontSize: 20, fontWeight: 600, margin: 0 }}>
                    {metrics.throughput
                      ? `${metrics.throughput.toFixed(1)} batches/min`
                      : t("worker.na", { defaultValue: "N/A" })}
                  </p>
                </div>
                <div
                  style={{
                    borderLeft: "4px solid #a855f7",
                    paddingLeft: 16,
                  }}
                >
                  <p style={{ fontSize: 13, color: "#666", marginBottom: 6 }}>
                    Error Rate
                  </p>
                  <p style={{ fontSize: 20, fontWeight: 600, margin: 0 }}>
                    {metrics.errorRate
                      ? `${metrics.errorRate.toFixed(2)}%`
                      : t("worker.na", { defaultValue: "N/A" })}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Worker Logs Modal */}
      {selectedWorkerLogs && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
          onClick={handleCloseLogs}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: 8,
              maxWidth: 900,
              width: "90%",
              maxHeight: "80vh",
              display: "flex",
              flexDirection: "column",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                padding: "20px 24px",
                borderBottom: "1px solid #e5e7eb",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <h2 style={{ fontSize: 20, fontWeight: 600, margin: 0 }}>
                  {selectedWorkerLogs.name} - Logs
                </h2>
                <button
                  className="drawer-close-btn"
                  onClick={handleCloseLogs}
                  aria-label="Close"
                  title="Close"
                >
                  <svg width="8" height="8" viewBox="0 0 8 8" fill="none" aria-hidden="true">
                    <line x1="1" y1="1" x2="7" y2="7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                    <line x1="7" y1="1" x2="1" y2="7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                  </svg>
                </button>
              </div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {["All", "Output", "Errors", "Events"].map((f) => (
                  <button
                    key={f}
                    onClick={() => setLogsFilter(f)}
                    style={{
                      padding: "3px 12px",
                      borderRadius: 12,
                      border: "1px solid",
                      borderColor: logsFilter === f ? "#6366f1" : "#d1d5db",
                      backgroundColor: logsFilter === f ? "#6366f1" : "white",
                      color: logsFilter === f ? "white" : "#374151",
                      fontSize: 12,
                      fontWeight: 500,
                      cursor: "pointer",
                    }}
                  >
                    {f === "Errors" ? "⚠ Errors" : f === "Events" ? "⚡ Events" : f === "Output" ? "📄 Output" : "All"}
                  </button>
                ))}
                {workerLogs?.logs && (
                  <span style={{ marginLeft: "auto", fontSize: 12, color: "#6b7280", alignSelf: "center" }}>
                    {(() => {
                      const filtered = workerLogs.logs.filter(log => {
                        if (logsFilter === "All") return true;
                        if (logsFilter === "Errors") return log.level === "Error";
                        if (logsFilter === "Events") return log.action !== "Output";
                        return log.action === logsFilter;
                      });
                      return `${filtered.length} of ${workerLogs.logs.length} entries`;
                    })()}
                  </span>
                )}
              </div>
            </div>
            <div
              style={{
                padding: 24,
                overflowY: "auto",
                flex: 1,
              }}
            >
              {loadingLogs ? (
                <div style={{ textAlign: "center", padding: 40 }}>
                  <p>Loading logs...</p>
                </div>
              ) : workerLogs &&
                workerLogs.logs &&
                workerLogs.logs.length > 0 ? (
                <>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: 13,
                    }}
                  >
                    <thead>
                      <tr
                        style={{
                          borderBottom: "2px solid #e5e7eb",
                          textAlign: "left",
                        }}
                      >
                        <th style={{ padding: "8px 12px", fontWeight: 600, color: "#6b7280" }}>
                          Timestamp
                        </th>
                        <th style={{ padding: "8px 12px", fontWeight: 600, color: "#6b7280" }}>
                          Action
                        </th>
                        <th style={{ padding: "8px 12px", fontWeight: 600, color: "#6b7280" }}>
                          Message
                        </th>
                        <th style={{ padding: "8px 12px", fontWeight: 600, color: "#6b7280" }}>
                          Performed By
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {workerLogs.logs
                        .filter(log => {
                          if (logsFilter === "All") return true;
                          if (logsFilter === "Errors") return log.level === "Error";
                          if (logsFilter === "Events") return log.action !== "Output";
                          return log.action === logsFilter;
                        })
                        .map((log) => (
                          <tr
                            key={log.id}
                            style={{
                              borderBottom: "1px solid #e5e7eb",
                              backgroundColor: log.level === "Error" ? "#fff5f5" : "transparent",
                            }}
                          >
                            <td style={{ padding: "8px 12px", whiteSpace: "nowrap" }}>
                              {new Date(log.timestamp).toLocaleString()}
                            </td>
                            <td style={{ padding: "8px 12px" }}>
                              <span
                                style={{
                                  padding: "2px 8px",
                                  borderRadius: 4,
                                  fontSize: 11,
                                  fontWeight: 600,
                                  backgroundColor:
                                    log.level === "Error"
                                      ? "#fee2e2"
                                      : log.action === "Started"
                                      ? "#d1fae5"
                                      : log.action === "Stopped"
                                      ? "#fee2e2"
                                      : log.action === "Paused"
                                      ? "#fef3c7"
                                      : log.action === "Created"
                                      ? "#dbeafe"
                                      : "#f3f4f6",
                                  color:
                                    log.level === "Error"
                                      ? "#991b1b"
                                      : log.action === "Started"
                                      ? "#065f46"
                                      : log.action === "Stopped"
                                      ? "#991b1b"
                                      : log.action === "Paused"
                                      ? "#92400e"
                                      : log.action === "Created"
                                      ? "#1e40af"
                                      : "#374151",
                                }}
                              >
                                {log.level === "Error" ? `⚠ ${log.action}` : log.action}
                              </span>
                            </td>
                            <td style={{ padding: "8px 12px", color: log.level === "Error" ? "#991b1b" : "inherit" }}>
                              {log.message}
                            </td>
                            <td style={{ padding: "8px 12px", color: "#6b7280" }}>
                              {log.performedBy ||
                                t("worker.systemUser", { defaultValue: "System" })}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                  {workerLogs.logs.length >= logsLimit && (
                    <div style={{ textAlign: "center", padding: "16px 0" }}>
                      <button
                        onClick={handleLoadMoreLogs}
                        disabled={loadingLogs}
                        style={{
                          padding: "6px 20px",
                          border: "1px solid #d1d5db",
                          borderRadius: 4,
                          backgroundColor: "white",
                          cursor: loadingLogs ? "default" : "pointer",
                          fontSize: 13,
                          color: "#374151",
                        }}
                      >
                        Load More ({logsLimit} loaded)
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div
                  style={{ textAlign: "center", padding: 40, color: "#6b7280" }}
                >
                  <p>No logs available for this worker</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Live Log Terminal Modal */}
      {liveLogsWorker && (
        <div
          style={{
            position: "fixed",
            top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: "rgba(0,0,0,0.7)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1100,
          }}
          onClick={handleCloseLiveLogs}
        >
          <div
            style={{
              backgroundColor: "#0d1117",
              borderRadius: 8,
              border: "1px solid #30363d",
              width: "90%",
              maxWidth: 1000,
              height: "75vh",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              boxShadow: "0 20px 60px rgba(0,0,0,0.6)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Terminal title bar */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "10px 16px",
                backgroundColor: "#161b22",
                borderBottom: "1px solid #30363d",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ color: "#a3e635", fontFamily: "monospace", fontSize: 13 }}>
                  &gt;_ {liveLogsWorker.name}
                </span>
                <span
                  style={{
                    display: "inline-block",
                    width: 8, height: 8,
                    borderRadius: "50%",
                    backgroundColor: "#22c55e",
                    animation: "pulse 2s infinite",
                  }}
                />
                <span style={{ color: "#6b7280", fontSize: 11 }}>LIVE</span>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  onClick={() => setLiveLines([])}
                  style={{
                    padding: "4px 10px",
                    fontSize: 11,
                    border: "1px solid #30363d",
                    borderRadius: 4,
                    backgroundColor: "transparent",
                    color: "#8b949e",
                    cursor: "pointer",
                  }}
                >
                  Clear
                </button>
                <button
                  className="drawer-close-btn"
                  onClick={handleCloseLiveLogs}
                  aria-label="Close"
                  title="Close"
                >
                  <svg width="8" height="8" viewBox="0 0 8 8" fill="none" aria-hidden="true">
                    <line x1="1" y1="1" x2="7" y2="7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                    <line x1="7" y1="1" x2="1" y2="7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                  </svg>
                </button>
              </div>
            </div>
            {/* Log output */}
            <div
              style={{
                flex: 1,
                overflowY: "auto",
                padding: "12px 16px",
                fontFamily: "monospace",
                fontSize: 12,
                lineHeight: 1.6,
                color: "#c9d1d9",
                backgroundColor: "#0d1117",
              }}
            >
              {liveLines.length === 0 ? (
                <div style={{ color: "#484f58", paddingTop: 8 }}>
                  Waiting for output from {liveLogsWorker.name}...
                </div>
              ) : (
                liveLines.map((line, i) => (
                  <div
                    key={i}
                    style={{
                      color: line.startsWith("[ERR]") || line.includes("ERROR") || line.includes("Exception")
                        ? "#f85149"
                        : line.includes("WARN") || line.includes("Warning")
                        ? "#d29922"
                        : line.includes("INFO") || line.includes("Information")
                        ? "#79c0ff"
                        : "#c9d1d9",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-all",
                    }}
                  >
                    {line}
                  </div>
                ))
              )}
              <div ref={liveLogEndRef} />
            </div>
            {/* Status bar */}
            <div
              style={{
                padding: "5px 16px",
                backgroundColor: "#161b22",
                borderTop: "1px solid #30363d",
                fontSize: 10,
                color: "#484f58",
                fontFamily: "monospace",
              }}
            >
              {liveLines.length} lines · auto-scroll enabled
            </div>
          </div>
        </div>
      )}

      {/* ── Gatherer Live Log Terminal Modal ── */}
      {liveLogsGatherer && (
        <div
          style={{
            position: "fixed",
            top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: "rgba(0,0,0,0.7)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1100,
          }}
          onClick={handleCloseGathererLiveLogs}
        >
          <div
            style={{
              backgroundColor: "#0d1117",
              borderRadius: 8,
              width: "min(900px, 95vw)",
              height: "70vh",
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 20px 60px rgba(0,0,0,0.8)",
              border: "1px solid #30363d",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Title bar */}
            <div
              style={{
                padding: "10px 16px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderBottom: "1px solid #30363d",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ color: "#a3e635", fontFamily: "monospace", fontSize: 13 }}>
                  &gt;_ {liveLogsGatherer.name}
                </span>
                <span
                  style={{
                    display: "inline-block",
                    width: 8, height: 8,
                    borderRadius: "50%",
                    backgroundColor: "#22c55e",
                    animation: "pulse 2s infinite",
                  }}
                />
                <span style={{ color: "#484f58", fontSize: 11, fontFamily: "monospace" }}>
                  LIVE
                </span>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  onClick={() => setGathererLiveLines([])}
                  style={{
                    padding: "3px 10px", fontSize: 11, border: "1px solid #30363d",
                    backgroundColor: "transparent", color: "#8b949e",
                    borderRadius: 4, cursor: "pointer",
                  }}
                >
                  Clear
                </button>
                <button
                  className="drawer-close-btn"
                  onClick={handleCloseGathererLiveLogs}
                  aria-label="Close"
                  title="Close"
                >
                  <svg width="8" height="8" viewBox="0 0 8 8" fill="none" aria-hidden="true">
                    <line x1="1" y1="1" x2="7" y2="7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                    <line x1="7" y1="1" x2="1" y2="7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                  </svg>
                </button>
              </div>
            </div>
            {/* Log output */}
            <div
              style={{
                flex: 1,
                overflowY: "auto",
                padding: "12px 16px",
                fontFamily: "monospace",
                fontSize: 12,
                lineHeight: 1.6,
                color: "#e6edf3",
                backgroundColor: "#0d1117",
              }}
            >
              {gathererLiveLines.length === 0 ? (
                <div style={{ color: "#484f58", paddingTop: 8 }}>
                  Waiting for output from {liveLogsGatherer.name}...
                </div>
              ) : (
                gathererLiveLines.map((line, i) => (
                  <div
                    key={i}
                    style={{
                      color: line.includes("[ERR]") ? "#f85149"
                           : line.includes("[stream error]") ? "#ffa657"
                           : "#e6edf3",
                      wordBreak: "break-all",
                    }}
                  >
                    {line}
                  </div>
                ))
              )}
              <div ref={gathererLiveLogEndRef} />
            </div>
            {/* Status bar */}
            <div
              style={{
                padding: "5px 16px",
                backgroundColor: "#161b22",
                borderTop: "1px solid #30363d",
                fontSize: 10,
                color: "#484f58",
                fontFamily: "monospace",
              }}
            >
              {gathererLiveLines.length} lines · auto-scroll enabled
            </div>
          </div>
        </div>
      )}

      {/* Host Selector Modal */}
      {hostSelectorOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
          onClick={closeHostSelector}
        >
          <div
            style={{
              backgroundColor: "white",
              padding: "24px",
              borderRadius: "8px",
              maxWidth: "500px",
              width: "90%",
              maxHeight: "80vh",
              overflow: "auto",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2
              style={{
                marginBottom: "16px",
                fontSize: "20px",
                fontWeight: "600",
              }}
            >
              {hostSelectorIsGatherer
                ? t("worker.selectGathererHost", { defaultValue: "Select Gatherer Host" })
                : t("worker.selectWorkerHost", { defaultValue: "Select Deployment Host" })}
            </h2>
            <p
              style={{
                marginBottom: "20px",
                color: "#6b7280",
                fontSize: "14px",
              }}
            >
              {hostSelectorIsGatherer
                ? t("worker.chooseGathererHost", { defaultValue: "Choose which host to deploy the gatherer to:" })
                : t("worker.chooseWorkerHost", { defaultValue: "Choose which host to deploy the worker to:" })}
            </p>

            <div
              style={{ display: "flex", flexDirection: "column", gap: "12px" }}
            >
              {hosts.map((host) => (
                <label
                  key={host.id}
                  style={{
                    display: "flex",
                    alignItems: "start",
                    padding: "16px",
                    border:
                      hostSelectorValue === host.id
                        ? "2px solid #2563eb"
                        : "1px solid #e5e7eb",
                    borderRadius: "6px",
                    cursor: "pointer",
                    backgroundColor:
                      hostSelectorValue === host.id ? "#eff6ff" : "white",
                  }}
                >
                  <input
                    type="radio"
                    name="host"
                    value={host.id}
                    checked={hostSelectorValue === host.id}
                    onChange={() => setHostSelectorValue(host.id)}
                    style={{ marginRight: "12px", marginTop: "4px" }}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: "600", marginBottom: "4px" }}>
                      {host.displayName || host.hostName}
                      {host.isLocal && (
                        <span
                          style={{
                            marginLeft: "8px",
                            fontSize: "12px",
                            backgroundColor: "#dbeafe",
                            color: "#1e40af",
                            padding: "2px 8px",
                            borderRadius: "4px",
                            fontWeight: "500",
                          }}
                        >
                          LOCAL
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: "13px", color: "#6b7280" }}>
                      {host.address} • {host.cpuCores} cores, {host.memoryGB} GB
                      RAM
                    </div>
                    {/* In service mode the install directory is the difference between a host that can
                        take a worker and one that will refuse at install time, so it is shown up front
                        rather than discovered by trying. */}
                    {hostSelectorServiceMode && (
                      <div
                        style={{
                          fontSize: "12px",
                          marginTop: "4px",
                          color: host.serviceInstallPath ? "#6b7280" : "#b45309",
                        }}
                      >
                        {host.serviceInstallPath
                          ? `${host.platform || t("worker.platformAuto", { defaultValue: "platform auto-detected" })} • ${host.serviceInstallPath}`
                          : t("worker.noInstallPath", {
                              defaultValue:
                                "No install directory set for this host — set it under Admin → Hosts first.",
                            })}
                      </div>
                    )}
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#9ca3af",
                        marginTop: "4px",
                      }}
                    >
                      Status:{" "}
                      {host.healthStatus ||
                        t("worker.unknown", { defaultValue: "Unknown" })}
                    </div>
                  </div>
                </label>
              ))}
            </div>

            <div
              style={{
                marginTop: "24px",
                display: "flex",
                gap: "12px",
                justifyContent: "flex-end",
              }}
            >
              <button
                onClick={closeHostSelector}
                disabled={actionInProgress}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#6b7280",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  cursor: actionInProgress ? "not-allowed" : "pointer",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmHostSelector}
                disabled={!hostSelectorValue || actionInProgress}
                style={{
                  padding: "10px 20px",
                  backgroundColor: hostSelectorValue ? "#2563eb" : "#9ca3af",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  cursor:
                    !hostSelectorValue || actionInProgress
                      ? "not-allowed"
                      : "pointer",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                {hostSelectorIsGatherer
                  ? t("worker.deployGatherer", { defaultValue: "Deploy Gatherer" })
                  : t("worker.deployWorker", { defaultValue: "Deploy Worker" })}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Worker App Settings Modal */}
      {workerSettingsModal && (
        <div
          style={{
            position: "fixed",
            top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1200,
          }}
          onClick={handleCloseWorkerSettings}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: 8,
              width: "90%",
              maxWidth: 600,
              maxHeight: "90vh",
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div
              style={{
                padding: "20px 24px",
                borderBottom: "1px solid #e5e7eb",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div>
                <h2 style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>
                  ⚙️ {workerSettingsModal.name} — App Settings
                </h2>
                <p style={{ fontSize: 13, color: "#6b7280", margin: "4px 0 0" }}>
                  {t("worker.settingsIntro", {
                    defaultValue:
                      "Settings for this worker alone. Leave a field blank to use the shared setting from Storage Configuration.",
                  })}
                </p>
              </div>
              <button
                className="drawer-close-btn"
                onClick={handleCloseWorkerSettings}
                aria-label="Close"
                title="Close"
              >
                <svg width="8" height="8" viewBox="0 0 8 8" fill="none" aria-hidden="true">
                  <line x1="1" y1="1" x2="7" y2="7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                  <line x1="7" y1="1" x2="1" y2="7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                </svg>
              </button>
            </div>

            {/* Body */}
            <div style={{ padding: "24px", overflowY: "auto", flex: 1 }}>
              {loadingWorkerSettings ? (
                <div style={{ textAlign: "center", padding: 40, color: "#6b7280" }}>
                  Loading settings...
                </div>
              ) : workerSettings ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

                  {/* Connection String */}
                  <div>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                      <label style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>
                        Database Connection String
                      </label>
                      <button
                        type="button"
                        onClick={() => setShowWorkerSecrets((v) => !v)}
                        style={{ background: "none", border: "none", color: "#4f46e5", fontSize: 12, fontWeight: 600, cursor: "pointer", padding: 0 }}
                      >
                        {showWorkerSecrets ? "Hide secrets" : "Show secrets"}
                      </button>
                    </div>
                    <input
                      type={showWorkerSecrets ? "text" : "password"}
                      value={workerSettings.connectionString ?? ""}
                      onChange={(e) => setWorkerSettings({ ...workerSettings, connectionString: e.target.value })}
                      // Inherited value as a placeholder, not as the field's value: pre-filling it
                      // would save the shared connection string back as this worker's own override.
                      placeholder={
                        workerSettings.inheritedConnectionString || "Server=...;Database=...;..."
                      }
                      autoComplete="off"
                      spellCheck={false}
                      style={{
                        width: "100%",
                        padding: "8px 12px",
                        border: "1px solid #d1d5db",
                        borderRadius: 6,
                        fontSize: 13,
                        fontFamily: "monospace",
                        boxSizing: "border-box",
                      }}
                    />
                    <p style={{ margin: "6px 0 0", fontSize: 11, color: "#6b7280" }}>
                      The password is masked (********) for security. Leave it masked to keep the current password, or retype the full value to change it.
                    </p>
                    <p style={{ margin: "4px 0 0", fontSize: 11, color: "#92400e" }}>
                      {t("worker.settingsConnRestart", {
                        defaultValue:
                          "A changed connection string only takes effect when the worker restarts — unlike the storage settings below.",
                      })}
                    </p>
                  </div>

                  {/* Batch Working Root — FileSystem only. The mode that decides this is the
                      EFFECTIVE one: with no override of its own, the worker follows the shared
                      setting, and hiding the field on a stale local default would leave an admin
                      unable to see the path their worker actually uses. */}
                  {(workerSettings.storageMode || workerSettings.inheritedStorageMode) !== "AzureBlob" && (
                  <div>
                    <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6, color: "#374151" }}>
                      Batch Working Root
                    </label>
                    <input
                      type="text"
                      value={workerSettings.batchWorkingRoot ?? ""}
                      onChange={(e) => setWorkerSettings({ ...workerSettings, batchWorkingRoot: e.target.value })}
                      // The inherited value is shown as a PLACEHOLDER, never as the field's value.
                      // Pre-filling it would turn "this worker inherits" into "this worker is
                      // pinned to today's shared path", so a later fleet-wide change would quietly
                      // skip this node.
                      placeholder={
                        workerSettings.inheritedBatchWorkingRoot ||
                        "/path/to/BATCH_WORKING"
                      }
                      maxLength={250}
                      style={{
                        width: "100%",
                        padding: "8px 12px",
                        border: "1px solid #d1d5db",
                        borderRadius: 6,
                        fontSize: 13,
                        fontFamily: "monospace",
                        boxSizing: "border-box",
                      }}
                    />
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6, flexWrap: "wrap" }}>
                      <p style={{ margin: 0, fontSize: 11, color: "#6b7280" }}>
                        {workerSettings.batchWorkingRoot
                          ? t("worker.settingsOverridesShared", {
                              value: workerSettings.inheritedBatchWorkingRoot || "—",
                              defaultValue:
                                "This worker will use the path above instead of the shared setting ({{value}}).",
                            })
                          : t("worker.settingsUsesShared", {
                              value: workerSettings.inheritedBatchWorkingRoot || "—",
                              defaultValue: "Blank — this worker uses the shared setting ({{value}}).",
                            })}
                      </p>
                      {workerSettings.batchWorkingRoot && (
                        <button
                          type="button"
                          onClick={() => setWorkerSettings({ ...workerSettings, batchWorkingRoot: "" })}
                          style={{ background: "none", border: "none", color: "#4f46e5", fontSize: 11, fontWeight: 600, cursor: "pointer", padding: 0 }}
                        >
                          {t("worker.settingsClearOverride", { defaultValue: "Clear override" })}
                        </button>
                      )}
                    </div>
                    <p style={{ margin: "4px 0 0", fontSize: 11, color: "#166534" }}>
                      {t("worker.settingsAppliesLive", {
                        defaultValue:
                          "Storage settings reach this worker on its next refresh (about a minute), whether it runs as a service or as a local process. No restart needed.",
                      })}
                    </p>
                  </div>
                  )}

                  {/* Storage Mode */}
                  <div>
                    <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6, color: "#374151" }}>
                      Storage Mode
                    </label>
                    <select
                      value={workerSettings.storageMode ?? ""}
                      onChange={(e) => setWorkerSettings({ ...workerSettings, storageMode: e.target.value })}
                      style={{
                        width: "100%",
                        padding: "8px 12px",
                        border: "1px solid #d1d5db",
                        borderRadius: 6,
                        fontSize: 13,
                        backgroundColor: "white",
                        boxSizing: "border-box",
                      }}
                    >
                      {/* "Inherit" is a real, selectable state rather than a blank that silently
                          means FileSystem — otherwise opening the dialog and pressing Save would
                          pin the worker to a mode nobody chose. */}
                      <option value="">
                        {t("worker.settingsInheritMode", {
                          value: workerSettings.inheritedStorageMode || "FileSystem",
                          defaultValue: "Use the shared setting ({{value}})",
                        })}
                      </option>
                      <option value="FileSystem">FileSystem</option>
                      <option value="AzureBlob">AzureBlob</option>
                    </select>
                  </div>

                  {/* Azure-only fields */}
                  {(workerSettings.storageMode || workerSettings.inheritedStorageMode) === "AzureBlob" && (
                    <>
                      <div>
                        <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6, color: "#374151" }}>
                          Azure Blob Connection String
                        </label>
                        <input
                          type={showWorkerSecrets ? "text" : "password"}
                          value={workerSettings.azureBlobConnectionString ?? ""}
                          onChange={(e) => setWorkerSettings({ ...workerSettings, azureBlobConnectionString: e.target.value })}
                          placeholder="DefaultEndpointsProtocol=https;AccountName=...;"
                          autoComplete="off"
                          spellCheck={false}
                          style={{
                            width: "100%",
                            padding: "8px 12px",
                            border: "1px solid #d1d5db",
                            borderRadius: 6,
                            fontSize: 13,
                            fontFamily: "monospace",
                            boxSizing: "border-box",
                          }}
                        />
                        <p style={{ margin: "6px 0 0", fontSize: 11, color: "#6b7280" }}>
                          The account key is masked (********). Leave it masked to keep the current key, or retype the full connection string to change it.
                        </p>
                      </div>

                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                        <div>
                          <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6, color: "#374151" }}>
                            Azure Main Root
                          </label>
                          <input
                            type="text"
                            value={workerSettings.azureMainRoot ?? ""}
                            onChange={(e) => setWorkerSettings({ ...workerSettings, azureMainRoot: e.target.value })}
                            placeholder="main"
                            style={{
                              width: "100%",
                              padding: "8px 12px",
                              border: "1px solid #d1d5db",
                              borderRadius: 6,
                              fontSize: 13,
                              boxSizing: "border-box",
                            }}
                          />
                        </div>
                        <div>
                          <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6, color: "#374151" }}>
                            Azure Staging Root
                          </label>
                          <input
                            type="text"
                            value={workerSettings.azureStagingRoot ?? ""}
                            onChange={(e) => setWorkerSettings({ ...workerSettings, azureStagingRoot: e.target.value })}
                            placeholder="staging"
                            style={{
                              width: "100%",
                              padding: "8px 12px",
                              border: "1px solid #d1d5db",
                              borderRadius: 6,
                              fontSize: 13,
                              boxSizing: "border-box",
                            }}
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {/* Verbose Logging */}
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "14px 16px", backgroundColor: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 8 }}>
                    <input
                      type="checkbox"
                      id="verboseLoggingToggle"
                      checked={workerSettings.verboseLogging ?? false}
                      onChange={(e) => setWorkerSettings({ ...workerSettings, verboseLogging: e.target.checked })}
                      style={{ marginTop: 2, width: 16, height: 16, cursor: "pointer", flexShrink: 0 }}
                    />
                    <label htmlFor="verboseLoggingToggle" style={{ cursor: "pointer" }}>
                      <span style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#374151" }}>Verbose Logging</span>
                      <span style={{ display: "block", fontSize: 12, color: "#6b7280", marginTop: 2 }}>
                        When disabled (default), idle poll-cycle messages are suppressed. Enable to see full diagnostic output.
                      </span>
                    </label>
                  </div>

                  {workerSettingsError && (
                    <div style={{ padding: "10px 14px", backgroundColor: "#fef2f2", border: "1px solid #fecaca", borderRadius: 6, color: "#991b1b", fontSize: 13 }}>
                      {workerSettingsError}
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ textAlign: "center", padding: 40, color: "#6b7280" }}>
                  {workerSettingsError || "Unable to load settings."}
                </div>
              )}
            </div>

            {/* Footer */}
            {workerSettings && (
              <div
                style={{
                  padding: "16px 24px",
                  borderTop: "1px solid #e5e7eb",
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  gap: 12,
                }}
              >
                {workerSettingsSaved && (
                  <span style={{ fontSize: 13, color: "#16a34a" }}>✓ Saved</span>
                )}
                <button
                  onClick={handleCloseWorkerSettings}
                  className="cancel-btn"
                  style={{ minWidth: 80 }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveWorkerSettings}
                  disabled={savingWorkerSettings}
                  className="go-btn"
                  style={{ minWidth: 100 }}
                >
                  {savingWorkerSettings ? "Saving..." : "Save"}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Gatherer Settings Modal ── */}
      {gathererSettingsModal && (
        <div
          style={{
            position: "fixed",
            top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1200,
          }}
          onClick={handleCloseGathererSettings}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: 8,
              width: "90%",
              maxWidth: 600,
              maxHeight: "90vh",
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div
              style={{
                padding: "20px 24px",
                borderBottom: "1px solid #e5e7eb",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div>
                <h2 style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>
                  ⚙️ {gathererSettingsModal.name} — Settings
                </h2>
                <p style={{ fontSize: 13, color: "#6b7280", margin: "4px 0 0" }}>
                  Overrides applied when this gatherer is started. Blank fields inherit the global configuration.
                </p>
              </div>
              <button
                className="drawer-close-btn"
                onClick={handleCloseGathererSettings}
                aria-label="Close"
                title="Close"
              >
                <svg width="8" height="8" viewBox="0 0 8 8" fill="none" aria-hidden="true">
                  <line x1="1" y1="1" x2="7" y2="7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                  <line x1="7" y1="1" x2="1" y2="7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                </svg>
              </button>
            </div>

            {/* Body */}
            <div style={{ padding: 24, overflowY: "auto", flex: 1 }}>
              {loadingGathererSettings ? (
                <div style={{ textAlign: "center", padding: 40, color: "#6b7280" }}>Loading settings...</div>
              ) : gathererSettings ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                    <div>
                      <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6, color: "#374151" }}>
                        Gatherer URL
                      </label>
                      <input
                        type="text"
                        value={gathererSettings.gathererUrl ?? ""}
                        onChange={(e) => setGathererSettings({ ...gathererSettings, gathererUrl: e.target.value })}
                        placeholder="http://0.0.0.0:20001"
                        style={{ width: "100%", padding: "8px 12px", border: "1px solid #d1d5db", borderRadius: 6, fontSize: 13, fontFamily: "monospace", boxSizing: "border-box" }}
                      />
                    </div>
                    <div>
                      <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6, color: "#374151" }}>
                        Service Port
                      </label>
                      <input
                        type="number"
                        value={gathererSettings.servicePort ?? 20001}
                        onChange={(e) => setGathererSettings({ ...gathererSettings, servicePort: parseInt(e.target.value) || 20001 })}
                        placeholder="20001"
                        style={{ width: "100%", padding: "8px 12px", border: "1px solid #d1d5db", borderRadius: 6, fontSize: 13, boxSizing: "border-box" }}
                      />
                    </div>
                  </div>

                  <div>
                    <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6, color: "#374151" }}>
                      Storage Mode
                    </label>
                    <select
                      value={gathererSettings.storageMode ?? ""}
                      onChange={(e) => setGathererSettings({ ...gathererSettings, storageMode: e.target.value })}
                      style={{ width: "100%", padding: "8px 12px", border: "1px solid #d1d5db", borderRadius: 6, fontSize: 13, backgroundColor: "white", boxSizing: "border-box" }}
                    >
                      {/* Inheriting is a real choice, not a blank that quietly means FileSystem. */}
                      <option value="">
                        {t("worker.settingsInheritMode", {
                          value: gathererSettings.inheritedStorageMode || "FileSystem",
                          defaultValue: "Use the shared setting ({{value}})",
                        })}
                      </option>
                      <option value="FileSystem">FileSystem</option>
                      <option value="AzureBlob">AzureBlob</option>
                    </select>
                  </div>

                  {/* No "Batch Working Root" here on purpose. A gatherer serves documents; it never
                      creates process/, success/ or error/, and nothing in it has ever read that key.
                      The field existed, accepted input and saved it — which is the same lie this
                      whole change is about, so it is gone rather than left looking like a setting.
                      The stored value is kept in the DTO so an existing row round-trips untouched. */}

                  {(gathererSettings.storageMode || gathererSettings.inheritedStorageMode) === "AzureBlob" && (
                    <>
                      <div>
                        <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6, color: "#374151" }}>
                          Azure Blob Connection String
                        </label>
                        <input
                          type="text"
                          value={gathererSettings.azureBlobConnectionString ?? ""}
                          onChange={(e) => setGathererSettings({ ...gathererSettings, azureBlobConnectionString: e.target.value })}
                          placeholder="DefaultEndpointsProtocol=https;AccountName=...;"
                          style={{ width: "100%", padding: "8px 12px", border: "1px solid #d1d5db", borderRadius: 6, fontSize: 13, fontFamily: "monospace", boxSizing: "border-box" }}
                        />
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                        <div>
                          <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6, color: "#374151" }}>
                            Azure Main Root
                          </label>
                          <input
                            type="text"
                            value={gathererSettings.azureMainRoot ?? ""}
                            onChange={(e) => setGathererSettings({ ...gathererSettings, azureMainRoot: e.target.value })}
                            placeholder="main"
                            style={{ width: "100%", padding: "8px 12px", border: "1px solid #d1d5db", borderRadius: 6, fontSize: 13, boxSizing: "border-box" }}
                          />
                        </div>
                        <div>
                          <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6, color: "#374151" }}>
                            Azure Staging Root
                          </label>
                          <input
                            type="text"
                            value={gathererSettings.azureStagingRoot ?? ""}
                            onChange={(e) => setGathererSettings({ ...gathererSettings, azureStagingRoot: e.target.value })}
                            placeholder="staging"
                            style={{ width: "100%", padding: "8px 12px", border: "1px solid #d1d5db", borderRadius: 6, fontSize: 13, boxSizing: "border-box" }}
                          />
                        </div>
                      </div>
                    </>
                  )}

                  <div>
                    <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6, color: "#374151" }}>
                      AFP→PDF Tool Path
                    </label>
                    <input
                      type="text"
                      value={gathererSettings.gmcPath ?? ""}
                      onChange={(e) => setGathererSettings({ ...gathererSettings, gmcPath: e.target.value })}
                      placeholder="/path/to/gmc or conversion tool"
                      style={{ width: "100%", padding: "8px 12px", border: "1px solid #d1d5db", borderRadius: 6, fontSize: 13, fontFamily: "monospace", boxSizing: "border-box" }}
                    />
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                    <div>
                      <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6, color: "#374151" }}>
                        JWT Secret
                      </label>
                      <input
                        type="text"
                        value={gathererSettings.jwtSecret ?? ""}
                        onChange={(e) => setGathererSettings({ ...gathererSettings, jwtSecret: e.target.value })}
                        placeholder="JWT signing secret"
                        style={{ width: "100%", padding: "8px 12px", border: "1px solid #d1d5db", borderRadius: 6, fontSize: 13, fontFamily: "monospace", boxSizing: "border-box" }}
                      />
                    </div>
                    <div>
                      <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6, color: "#374151" }}>
                        CORS Client URL
                      </label>
                      <input
                        type="text"
                        value={gathererSettings.clientUrl ?? ""}
                        onChange={(e) => setGathererSettings({ ...gathererSettings, clientUrl: e.target.value })}
                        placeholder="http://localhost:3000"
                        style={{ width: "100%", padding: "8px 12px", border: "1px solid #d1d5db", borderRadius: 6, fontSize: 13, fontFamily: "monospace", boxSizing: "border-box" }}
                      />
                    </div>
                  </div>

                  {gathererSettingsError && (
                    <div style={{ padding: "10px 14px", backgroundColor: "#fef2f2", border: "1px solid #fecaca", borderRadius: 6, color: "#991b1b", fontSize: 13 }}>
                      {gathererSettingsError}
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ textAlign: "center", padding: 40, color: "#6b7280" }}>
                  {gathererSettingsError || "Unable to load settings."}
                </div>
              )}
            </div>

            {/* Footer */}
            {gathererSettings && (
              <div
                style={{
                  padding: "16px 24px",
                  borderTop: "1px solid #e5e7eb",
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  gap: 12,
                }}
              >
                {gathererSettingsSaved && (
                  <span style={{ fontSize: 13, color: "#16a34a" }}>✓ Saved</span>
                )}
                <button onClick={handleCloseGathererSettings} className="cancel-btn" style={{ minWidth: 80 }}>
                  Cancel
                </button>
                <button
                  onClick={handleSaveGathererSettings}
                  disabled={savingGathererSettings}
                  className="go-btn"
                  style={{ minWidth: 100 }}
                >
                  {savingGathererSettings ? "Saving..." : "Save"}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Confirm modal ── */}
      {serviceCheck && (
        <div className="dbModalOverlay" onClick={() => setServiceCheck(null)}>
          <div className="dbModalCard" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 460 }}>
            <button
              className="drawer-close-btn drawer-close-btn-abs"
              onClick={() => setServiceCheck(null)}
              aria-label="Close"
            >
              <svg width="8" height="8" viewBox="0 0 8 8" fill="none" aria-hidden="true">
                <line x1="1" y1="1" x2="7" y2="7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                <line x1="7" y1="1" x2="1" y2="7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
              </svg>
            </button>
            <div className="dbModalIcon">{serviceCheck.running ? "\u2713" : "!"}</div>
            <div className="dbModalTitle">{serviceCheck.workerName}</div>
            <div className="dbModalDesc">{serviceCheck.message}</div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 24 }}>
              <button type="button" className="primary-btn" onClick={() => setServiceCheck(null)}>
                {t("common.close", { defaultValue: "Close" })}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmModal && (
        <div className="dbModalOverlay" onClick={() => setConfirmModal(null)}>
          <div className="dbModalCard" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 420 }}>
            <button
              className="drawer-close-btn drawer-close-btn-abs"
              onClick={() => setConfirmModal(null)}
              aria-label="Close"
            >
              <svg width="8" height="8" viewBox="0 0 8 8" fill="none" aria-hidden="true">
                <line x1="1" y1="1" x2="7" y2="7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                <line x1="7" y1="1" x2="1" y2="7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
              </svg>
            </button>
            <div className="dbModalIcon">{confirmModal.icon}</div>
            <div className="dbModalTitle">{confirmModal.workerName}</div>
            <div className="dbModalDesc">{confirmModal.message}</div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 24 }}>
              <button type="button" className="dbModalCancel" onClick={() => setConfirmModal(null)}>
                Cancel
              </button>
              <button type="button" className="primary-btn" onClick={confirmModal.onConfirm}>
                Continue
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
